import { createDraftAutosave } from "../lib/draft-autosave";
import { forwardSubject, replySubject } from "../lib/mail-subject";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { infiniteQueryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { Archive, ArchiveRestore, Building2, CalendarPlus, Check, Ellipsis, ExternalLink, Forward, Mail, Paperclip, Pencil, Reply, ReplyAll, SendHorizontal, Star, Tag, Target, Trash2, UserPlus, UserRound, X } from "lucide-react";
import type { EmailEditorRef } from "@react-email/editor";
import DateTimePicker from "../components/DateTimePicker";
import EmailAttachmentList from "../components/EmailAttachmentList";
import ReceivedAttachments from "../components/ReceivedAttachments";
import InviteCard from "../components/InviteCard";
import RichEmailEditor from "../components/RichEmailEditor";
import RecipientEditor, {
    parseRecipients,
    recipientsToHeader,
    emptyRecipientBuckets,
    type RecipientBuckets,
} from "../components/RecipientEditor";
import SignaturePicker, { insertSignatureAtCursor } from "../components/SignaturePicker";
import { ActionBar } from "@/ui";
import { FocusSplit } from "@/ui";
import { FocusExpandButton } from "@/ui";
import LinkedItems from "../components/LinkedItems";
import { useWorkspaceContext } from "@/ui";
import { PaginationFooter } from "../components/collection/PaginationFooter";
import SuggestedTimesModal from "../components/SuggestedTimesModal";
import {
    suggestedTimesInsertContent,
    type SuggestedTimesAttrs,
} from "../components/editor/SuggestedTimesNode";
import { captureEditorPos, insertAtPos, type EditorPos } from "../lib/editor-insert";
import { formatFullDateTime, formatLocalDateTime, formatMailboxDate } from "../lib/dates";
import {
    COMPANIES_QUERY_KEY,
    CONTACTS_QUERY_KEY,
    LEADS_QUERY_KEY,
    MAILBOX_LABELS_QUERY_KEY,
    MAILBOX_SUMMARY_QUERY_KEY,
    MAILBOX_THREADS_QUERY_KEY,
    MESSAGES_QUERY_KEY,
} from "../lib/query-keys";
import { upsertDirectoryRecord } from "../lib/directory-cache";
import { findCachedMessageByHeader, patchMailboxThreadLabels, removeMailboxThread, patchMailboxThreadState, removeMailboxMessage, replaceMailboxMessage, setMailboxUnreadTotal, snapshotMailbox, upsertMailboxMessages, type MailboxStateOperation } from "../lib/mailbox-cache";
import { useMailUnreadCount } from "../lib/use-mail-unread-count";
import { sanitizeEmailHtml } from "../lib/sanitize-email-html";
import { EmailFrame } from "../components/EmailFrame";
import { Accordion, Button, ContextMenu, Menu, SelectBox, Tooltip, toast } from "@/ui";
import {
    contacts as contactsApi,
    companies as companiesApi,
    leads as leadsApi,
    meetings as meetingsApi,
    messages as messagesApi,
    mailbox,
    messageRecipients as messageRecipientsApi,
    products as productsApi,
    userSettings as userSettingsApi,
    directoryQueryOptions,
    type Contact,
    type Company,
    type Lead,
    type Meeting,
    type MeetingActivity,
    type Message,
    type MailboxThread,
    type MailboxThreadsResponse,
    type MailboxSummary,
    type MailboxView,
    type ProductKey,
    type EmailAttachment,
    type SignatureRecord,
} from "../lib/api";

import {
    getSendAsHtml, setSendAsHtml,
    getBccOutbound, setBccOutbound,
    getSelectedProduct, setSelectedProduct,
    getArchivedThreads, archiveThread, archiveThreads, unarchiveThread, type ArchivedSet,
    getLegacyMailboxPreferences, markLegacyMailboxPreferencesImported,
} from "../lib/prefs";
import {
    prepareEmailAttachmentsForSend,
    removeEditorImageBySrc,
    revokeAttachmentPreviews,
    uploadEmailAttachment,
    validateAttachmentSelection,
    type UploadedEmailAttachment,
} from "../lib/email-attachments";
import { settingsQueryKey } from "./Settings";

const MAILBOX_PAGE_SIZE = 25;
const THREAD_PREFETCH_CONCURRENCY = 4;

function threadMessagesQueryOptions(threadKey: string) {
    return infiniteQueryOptions({
        queryKey: [...MESSAGES_QUERY_KEY, "thread", threadKey],
        queryFn: ({ pageParam }) => messagesApi.list({
            limit: "100",
            sort: "-createdAt",
            threadKey,
            ...(pageParam ? { starting_after: pageParam } : {}),
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: lastPage => lastPage.pagination?.nextCursor ?? undefined,
    });
}

/** Known SES message statuses we surface in the UI. `any` is the "no filter"
 *  sentinel for the Sent view's dropdown. */
type StatusFilter = "any" | "delivered" | "sent" | "bounced" | "complained" | "queued";
type MailSort = "newest" | "oldest" | "counterparty" | "subject";
type MailGroup = "none" | "date" | "counterparty" | "read" | "status";
type MailboxQuickFilter = "all" | "unread" | "to-me" | "cc-me" | `product:${ProductKey}`;

interface BadgeStyle { label: string; bg: string; fg: string; border?: string }
const STATUS_STYLES: Record<string, BadgeStyle> = {
    draft:      { label: "Draft · not sent", bg: "#fef3c7", fg: "#92400e", border: "1px dashed #d97706" },
    delivered:  { label: "Delivered",  bg: "#dcfce7", fg: "#166534" },
    sent:       { label: "Sent",       bg: "#e0e7ff", fg: "#3730a3" },
    queued:     { label: "Queued",     bg: "#fef3c7", fg: "#854d0e" },
    bounced:    { label: "Bounced",    bg: "#fee2e2", fg: "#991b1b" },
    complained: { label: "Complaint",  bg: "#ffedd5", fg: "#9a3412" },
    failed:     { label: "Failed",     bg: "#fee2e2", fg: "#991b1b" },
    received:   { label: "Received",   bg: "#e0f2fe", fg: "#075985" },
};

function StatusBadge({ status }: { status: string | null | undefined }) {
    if (!status) return null;
    const style = STATUS_STYLES[status] ?? { label: status, bg: "#f3f4f6", fg: "#6b7280" };
    return (
        <span style={{
            display: "inline-block",
            background: style.bg,
            color: style.fg,
            border: style.border ?? "1px solid transparent",
            whiteSpace: "nowrap",
            fontSize: 11,
            fontWeight: 600,
            padding: "1px 8px",
            borderRadius: 10,
            lineHeight: 1.6,
            textTransform: "uppercase",
            letterSpacing: 0.2,
        }}>
            {style.label}
        </span>
    );
}

/** Status → icon glyph, color, and human label. */
const STATUS_ICON: Record<string, { char: string; color: string; label: string }> = {
    draft:      { char: "✎", color: "#d97706", label: "Draft (not sent)" },
    delivered:  { char: "✓", color: "#16a34a", label: "Delivered" },
    sent:       { char: "•", color: "#9ca3af", label: "Sent (not yet delivered)" },
    queued:     { char: "•", color: "#d97706", label: "Queued" },
    bounced:    { char: "✕", color: "#dc2626", label: "Bounced" },
    complained: { char: "✕", color: "#ea580c", label: "Complaint" },
    failed:     { char: "✕", color: "#dc2626", label: "Failed" },
    received:   { char: "↓", color: "#0369a1", label: "Received" },
};
function iconFor(status: string | null | undefined) {
    return STATUS_ICON[status ?? ""] ?? { char: "•", color: "#9ca3af", label: status || "Unknown" };
}

/**
 * Delivery status with a tooltip. `detailed` (the open conversation) and
 * drafts render a labelled pill so Draft vs Sent is unmistakable; dense list
 * rows keep the compact glyph (✓ delivered / ✕ bounced / • pending). For
 * multi-recipient (group) sends the tooltip lists each recipient's status.
 */
function DeliveryStatus({ message, detailed = false }: { message: Message; detailed?: boolean }) {
    const isDraft = message.status === "draft";
    const asPill = detailed || isDraft;
    const multi = message.toAddress.includes(",");
    const { data: recipients } = useQuery({
        queryKey: ["message-recipients", message.id],
        queryFn: async () => (await messageRecipientsApi.list({ messageId: message.id, limit: "200" })).data,
        staleTime: 15_000,
        enabled: detailed && multi,
    });
    const meta = iconFor(message.status);
    const perRecipient = detailed && multi && recipients && recipients.length > 0 ? recipients : null;
    return (
        <Tooltip.Root>
            <Tooltip.Trigger
                render={asPill
                    ? <span aria-label={meta.label} style={{ cursor: "default", lineHeight: 1 }}><StatusBadge status={message.status} /></span>
                    : (
                        <span
                            aria-label={meta.label}
                            style={{ color: meta.color, fontWeight: 700, fontSize: 14, lineHeight: 1, cursor: "default" }}
                        >
                            {meta.char}
                        </span>
                    )}
            />
            <Tooltip.Content>
                {isDraft ? (
                    <span>Not sent yet — open the draft to edit and send</span>
                ) : perRecipient ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {perRecipient.map(r => {
                            const m = iconFor(r.status);
                            return (
                                <div key={r.id} style={{ whiteSpace: "nowrap" }}>
                                    <span style={{ color: m.color }}>{m.char}</span> {r.address} — {m.label.toLowerCase()}
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <span>{message.toAddress} — {meta.label.toLowerCase()}</span>
                )}
            </Tooltip.Content>
        </Tooltip.Root>
    );
}

// Public R2 domain for email assets. bodyHtml ships `cid:` refs (which only an
// email client can resolve), so the web viewer swaps each to its public URL
// using the message's inlineAssets map.
const FILES_BASE = "https://files.os.kardoe.com";

function resolveInlineImages(
    html: string,
    inlineAssets: { contentId: string; key: string }[] | null | undefined,
): string {
    if (!inlineAssets?.length || !html.includes("cid:")) return html;
    let out = html;
    for (const { contentId, key } of inlineAssets) {
        const url = `${FILES_BASE}/${key.split("/").map(encodeURIComponent).join("/")}`;
        // Replace src="cid:<id>" (and the bare cid) — contentId has no regex
        // metachars in practice (asset-<uuid>@domain), but escape defensively.
        const escaped = contentId.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        out = out.replace(new RegExp(`cid:${escaped}`, "g"), url);
    }
    return out;
}

/*
 * Quoted-history detection. No RFC covers this — every client marks its
 * quotes differently, so (like Gmail/Apple Mail themselves) we go by the
 * known per-client containers, then fall back to a blockquote introduced by
 * a reply preamble ("On … wrote:", "Original Message", …).
 */
const QUOTE_SELECTORS = [
    "div.gmail_quote",            // Gmail
    "blockquote.gmail_quote",
    'blockquote[type="cite"]',    // Apple Mail / iOS
    "#divRplyFwdMsg",             // Outlook (web/desktop)
    "#appendonsend",
    "#OLK_SRC_BODY_SECTION",      // legacy Outlook
    "div.yahoo_quoted",           // Yahoo
    "div.moz-cite-prefix",        // Thunderbird
].join(", ");

const PREAMBLE_RE = /(wrote|schrieb|a écrit|escribió):\s*$|original message|forwarded message/i;

function splitQuotedHtml(html: string): { main: string; quoted: string | null } {
    const doc = new DOMParser().parseFromString(html, "text/html");
    let marker: Element | null = doc.body.querySelector(QUOTE_SELECTORS);
    if (!marker) {
        // Generic blockquotes only count when a reply preamble introduces them
        // — newsletters use blockquotes for legitimate content.
        for (const bq of doc.body.querySelectorAll("blockquote")) {
            const prev = bq.previousElementSibling;
            if (prev && PREAMBLE_RE.test((prev.textContent ?? "").trim().slice(-120))) { marker = bq; break; }
        }
    }
    if (!marker) return { main: html, quoted: null };
    // Fold the "On … wrote:" lead line into the hidden part too.
    let start: Node = marker;
    const prev = marker.previousElementSibling;
    if (prev && PREAMBLE_RE.test((prev.textContent ?? "").trim().slice(-120))) start = prev;
    // Range handles markers nested inside wrapper divs by splitting ancestors.
    const range = doc.createRange();
    range.setStartBefore(start);
    range.setEndAfter(doc.body.lastChild ?? start);
    const holder = doc.createElement("div");
    holder.appendChild(range.extractContents());
    // If nothing readable remains, the message WAS the quote — don't trim.
    if (!(doc.body.textContent ?? "").trim()) return { main: html, quoted: null };
    // A leading sender <style> parses into <head>; both halves keep it.
    const styles = Array.from(doc.head.querySelectorAll("style"), el => el.outerHTML).join("");
    return { main: styles + doc.body.innerHTML, quoted: styles + holder.innerHTML };
}

function splitQuotedText(text: string): { main: string; quoted: string | null } {
    const lines = text.split("\n");
    const isQuoteStart = (l: string) =>
        /^\s*>/.test(l)
        || /^On .{0,200}wrote:\s*$/.test(l)
        || /^-{2,}\s*Original Message\s*-{2,}/i.test(l)
        || /^-{3,}\s*Forwarded message\s*-{3,}/i.test(l)
        || /^_{8,}\s*$/.test(l);
    const idx = lines.findIndex(isQuoteStart);
    if (idx <= 0) return { main: text, quoted: null };
    return { main: lines.slice(0, idx).join("\n").trimEnd(), quoted: lines.slice(idx).join("\n") };
}

interface Thread {
    threadKey: string;
    latest: Message;
    count: number;
    hasInbound: boolean;
    participants: Array<{ address: string; name: string | null }>;
    /** ISO timestamp of the newest *inbound* row in this thread; null if none. */
    latestInboundTs: string | null;
    unread: boolean;
    archived: boolean;
    starred: boolean;
    trashed: boolean;
    labels: MailboxThread["labels"];
}

const EMPTY_DOC = { type: "doc", content: [{ type: "paragraph", content: [] }] };

/** Extract the paragraph-level nodes from a TipTap signature doc and return
 *  them verbatim. Tolerates the doc being null, missing a `content` array,
 *  or being legacy plain text (returns nothing).
 *
 *  No auto-prepended `-- ` delimiter — if the user wants one, they put it
 *  in their saved signature on /settings. The compose flow splices these
 *  nodes into the initial editor doc as-is. */
export function signatureNodes(signature: unknown): any[] {
    if (!signature || typeof signature !== "object") return [];
    const doc = signature as { type?: string; content?: unknown[] };
    if (!Array.isArray(doc.content) || doc.content.length === 0) return [];
    return doc.content;
}

function buildInitialDoc(opts: { lead?: string; quoted?: string; signature?: unknown }) {
    const content: any[] = [{ type: "paragraph", content: [] }];
    if (opts.lead) {
        content.push({ type: "paragraph", content: [{ type: "text", text: opts.lead }] });
    }
    // Signature sits between the cursor's starting paragraph and the quoted
    // body so the user types above the sig and the sig appears above the quote.
    for (const node of signatureNodes(opts.signature)) content.push(node);
    if (opts.quoted) {
        // CRLF bodies (Outlook, most plain-text mail) would otherwise leave a
        // lone "\r" on every line, each rendering as a blank quoted paragraph.
        const lines = opts.quoted.split(/\r\n|\r|\n/).filter(line => line.trim());
        content.push({
            type: "blockquote",
            content: lines.map((l) => ({ type: "paragraph", content: [{ type: "text", text: l }] })),
        });
    }
    return { type: "doc", content };
}

interface ComposerCtx {
    mode:       "new" | "reply" | "replyAll" | "forward";
    to:         string;
    cc?:        string;
    bcc?:       string;
    subject:    string;
    inReplyTo?: string;
    productKey: ProductKey | null;
    quotedLead: string;
    quotedBody: string;
    draftId?: string;
    draftBodyHtml?: string;
    draftAttachments?: EmailAttachment[];
}

interface MailboxComposePrefill {
    mode?: ComposerCtx["mode"];
    to?: string;
    cc?: string;
    bcc?: string;
    subject?: string;
    inReplyTo?: string;
    productKey?: ProductKey;
    quotedLead?: string;
    quotedBody?: string;
    draftId?: string;
    draftBodyHtml?: string;
    draftAttachments?: EmailAttachment[];
}

interface MailDraft {
    id: string;
    ctx: ComposerCtx;
    title: string;
}

interface MeetingPopoverContext {
    productKey: ProductKey | null;
    title: string;
    attendees: string[];
}

/** Pull bare email tokens out of a To: header — handles `Name <addr>` form. */
function extractAddrs(s: string): string[] {
    const out = new Set<string>();
    for (const m of s.matchAll(/<([^>]+)>|([^\s,;<>"'()]+@[^\s,;<>"'()]+)/g)) {
        const addr = (m[1] ?? m[2] ?? "").trim().toLowerCase();
        if (addr) out.add(addr);
    }
    return [...out];
}

/** Same header, but keeping the display names: `"Ada" <ada@x.com>, bob@y.com`. */
export function parsePeople(header?: string | null): { address: string; name: string | null }[] {
    const out: { address: string; name: string | null }[] = [];
    for (const part of (header ?? "").split(/,(?=(?:[^"]*"[^"]*")*[^"]*$)/)) {
        const address = extractAddrs(part)[0];
        if (!address || out.some(p => p.address === address)) continue;
        const name = part.replace(/<[^>]*>/g, "").replace(/["']/g, "").trim();
        out.push({ address, name: name && name.toLowerCase() !== address ? name : null });
    }
    return out;
}

function isSelfAddr(a: string, selfAddresses: Set<string>): boolean {
    return selfAddresses.has(a.toLowerCase());
}

function tsOf(m: Message): string {
    return m.receivedAt ?? m.sentAt ?? m.createdAt;
}

function dateGroupLabel(timestamp: string): string {
    const date = new Date(timestamp);
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const dayDifference = Math.round((startOfToday.getTime() - startOfDate.getTime()) / 86_400_000);
    if (dayDifference === 0) return "Today";
    if (dayDifference === 1) return "Yesterday";
    if (dayDifference > 1 && dayDifference < 7) return "Earlier this week";
    if (date.getFullYear() === today.getFullYear() && date.getMonth() === today.getMonth()) return "Earlier this month";
    return new Intl.DateTimeFormat(undefined, { month: "long", year: "numeric" }).format(date);
}

// "YYYY-MM-DD HH:MM" in the viewer's local time (see lib/dates).
const fmtLocal = formatLocalDateTime;

function quotePreamble(m: Message): string {
    const when = fmtLocal(tsOf(m));
    const who  = m.fromName ? `${m.fromName} <${m.fromAddress}>` : m.fromAddress;
    return `On ${when}, ${who} wrote:`;
}

/**
 * Flatten an HTML body to plain text for the quoted reply. Inbound HTML-only
 * mail (Apple Mail, Outlook, etc.) carries no text/plain part, so `bodyText`
 * is null — without this the quote would be empty below the "On … wrote:" lead.
 * Block elements and <br> become newlines so the blockquote keeps its shape;
 * runs of blank lines are collapsed.
 */
function htmlToText(html: string | null | undefined): string {
    if (!html) return "";
    const doc = new DOMParser().parseFromString(html, "text/html");
    doc.querySelectorAll("br").forEach((br) => br.replaceWith("\n"));
    doc.querySelectorAll("p, div, tr, li, h1, h2, h3, h4, h5, h6, blockquote")
        .forEach((el) => el.append("\n"));
    return (doc.body.textContent ?? "")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
}

/** Quoted-body source for a reply/forward: prefer the plain-text part, fall
 *  back to flattening the HTML when the message has no text/plain. */
function quotedBodyOf(m: Message): string {
    return (m.bodyText && m.bodyText.trim()) ? m.bodyText : htmlToText(m.bodyHtml);
}

/** RSVP is no longer tracked (no calendar provider) — only the meeting itself
 *  and our own cancellation are authoritative. */
function meetingActivityLabel(activity: MeetingActivity): string {
    return activity.responseStatus === "cancelled"
        ? `Cancelled: ${activity.title}`
        : `Meeting with this contact: ${activity.title}`;
}

function meetingActivityDate(activity: MeetingActivity): string {
    return new Intl.DateTimeFormat(undefined, {
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: activity.timeZone,
    }).format(new Date(activity.startAt));
}

interface MailboxProps {
    title:    string;
    /** Presentation mode: sender-oriented Inbox or recipient-oriented Sent. */
    show:     "inbound" | "outboundOnly";
    emptyMsg: string;
    /** Path prefix for thread deep links — "/inbox" or "/sent". */
    basePath: string;
    /** First-class server projection view. */
    view: MailboxView;
    labelId?: string;
}

export interface ContactFocus {
    email: string;
    fallbackName: string;
    productKey: ProductKey | null;
}

function usableName(name?: string | null): string | null {
    const value = name?.trim();
    return value || null;
}

function initialsOf(name: string): string {
    return name.split(/\s+|@/).filter(Boolean).slice(0, 2).map(part => part[0]?.toUpperCase()).join("") || "?";
}

function splitPersonName(name: string): { firstName: string | null; lastName: string | null } {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length < 2) return { firstName: parts[0] ?? null, lastName: null };
    return { firstName: parts.shift() ?? null, lastName: parts.join(" ") || null };
}

const PERSONAL_EMAIL_DOMAINS = new Set([
    "gmail.com", "googlemail.com", "outlook.com", "hotmail.com", "live.com",
    "icloud.com", "me.com", "yahoo.com", "proton.me", "protonmail.com",
]);

function inferredCompanyName(email: string): string | null {
    const domain = email.split("@").at(-1)?.trim().toLowerCase();
    if (!domain || PERSONAL_EMAIL_DOMAINS.has(domain)) return null;
    const stem = domain.split(".")[0].replace(/[-_]+/g, " ").trim();
    return stem ? stem.replace(/\b\w/g, character => character.toUpperCase()) : null;
}

/** One-to-two-line list preview. A cheap tag strip is fine here — it's a
 *  snippet, not a renderer (the viewer uses the real HTML body). */
function previewText(m: Message): string {
    // Trim quoted history from the plain-text part so the snippet shows the
    // new content, not "On … wrote:".
    const text = (m.bodyText?.trim() && splitQuotedText(m.bodyText).main.trim())
        || (m.bodyHtml ?? "")
            .replace(/<(style|script)[\s\S]*?<\/\1>/gi, " ")
            .replace(/<[^>]+>/g, " ")
            .replace(/&nbsp;/gi, " ");
    return text.replace(/\s+/g, " ").trim().slice(0, 200);
}

/** Icon toolbar button with a tooltip — the thread header's action style. */
function IconAction({ label, onClick, children }: { label: string; onClick: () => void; children: React.ReactNode }) {
    return (
        <Tooltip.Root>
            <Tooltip.Trigger render={<Button variant="ghost" size="sm" className="conversation-action" aria-label={label} onClick={onClick} />}>
                {children}
            </Tooltip.Trigger>
            <Tooltip.Content>{label}</Tooltip.Content>
        </Tooltip.Root>
    );
}

function contactDisplayName(contact: Contact | undefined, fallbackName: string, email: string): string {
    return usableName(contact?.name)
        ?? usableName([usableName(contact?.firstName), usableName(contact?.lastName)].filter(Boolean).join(" "))
        ?? usableName(fallbackName)
        ?? email;
}

export default function Mailbox({ title, show, emptyMsg, basePath, view, labelId }: MailboxProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const queryClient = useQueryClient();
    const { openContext, closeContext } = useWorkspaceContext();
    const params = useParams<{ threadId?: string }>();
    const urlThreadKey = params.threadId ? decodeURIComponent(params.threadId) : null;

    const [drafts, setDrafts] = useState<MailDraft[]>([]);
    const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
    const [meetingOpen, setMeetingOpen] = useState(false);
    const [archived, setArchived] = useState<ArchivedSet>(() => getArchivedThreads());
    const [trashed, setTrashed] = useState<ArchivedSet>({});
    const [starred, setStarred] = useState<ArchivedSet>({});
    const [quickFilter, setQuickFilter] = useState<MailboxQuickFilter>("all");
    const [selected, setSelected] = useState<Set<string>>(() => new Set());
    /** Status filter — only meaningful in Sent. Filters threads by the
     *  status of their latest outbound message. "any" disables the filter. */
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("any");
    const [focusTwoExpanded, setFocusTwoExpanded] = useState(false);
    const [contactFocus, setContactFocus] = useState<ContactFocus | null>(null);
    const [threadSearch, setThreadSearch] = useState("");
    const [page, setPage] = useState(0);
    const [tableDensity, setTableDensity] = useState<"comfortable" | "compact">(() =>
        localStorage.getItem("kardoe.mail.density") === "compact" ? "compact" : "comfortable",
    );
    const [mailLayout, setMailLayout] = useState<"list" | "table">(() =>
        localStorage.getItem("kardoe.mail.layout") === "table" ? "table" : "list",
    );
    const preferenceScope = show === "inbound" ? "inbox" : "sent";
    const [mailSort, setMailSort] = useState<MailSort>(() => {
        const saved = localStorage.getItem(`kardoe.mail.${preferenceScope}.sort`);
        return saved === "oldest" || saved === "counterparty" || saved === "subject" ? saved : "newest";
    });
    const [mailGroup, setMailGroup] = useState<MailGroup>(() => {
        const saved = localStorage.getItem(`kardoe.mail.${preferenceScope}.group`);
        return saved === "date" || saved === "counterparty" || saved === "read" || saved === "status" ? saved : "none";
    });
    const stateMigrationAttempted = useRef(false);
    const lastMarkedReadKey = useRef<string | null>(null);
    const composeLocationKey = useRef<string | null>(null);

    const mailboxRequestView: MailboxView = view === "inbox" && quickFilter === "unread" ? "unread" : view;
    const recipientRole = quickFilter === "to-me" || quickFilter === "cc-me" ? quickFilter : undefined;
    const productFilter = quickFilter.startsWith("product:")
        ? quickFilter.slice("product:".length) as ProductKey
        : undefined;
    const serverMailboxQuery = useInfiniteQuery({
        queryKey: [...MAILBOX_THREADS_QUERY_KEY, mailboxRequestView, labelId, threadSearch, quickFilter],
        queryFn: ({ pageParam }) => mailbox.threads({
            view: mailboxRequestView,
            labelId,
            search: threadSearch || undefined,
            recipientRole,
            productKey: productFilter,
            limit: MAILBOX_PAGE_SIZE,
            cursor: pageParam,
        }),
        initialPageParam: undefined as string | undefined,
        getNextPageParam: lastPage => lastPage.pagination.nextCursor ?? undefined,
    });
    const labelsQuery = useQuery({
        queryKey: MAILBOX_LABELS_QUERY_KEY,
        queryFn: mailbox.labels,
        staleTime: 60_000,
    });

    useEffect(() => localStorage.setItem("kardoe.mail.density", tableDensity), [tableDensity]);
    useEffect(() => localStorage.setItem("kardoe.mail.layout", mailLayout), [mailLayout]);
    useEffect(() => localStorage.setItem(`kardoe.mail.${preferenceScope}.sort`, mailSort), [mailSort, preferenceScope]);
    useEffect(() => localStorage.setItem(`kardoe.mail.${preferenceScope}.group`, mailGroup), [mailGroup, preferenceScope]);
    useEffect(() => setQuickFilter("all"), [labelId, view]);
    useEffect(() => setPage(0), [mailboxRequestView, labelId, threadSearch, statusFilter, quickFilter]);

    // If this account predates server-backed mailbox state, import the old
    // browser cache once. Current mailbox responses already include state, so
    // there is no reason to download every state row on each mount.
    useEffect(() => {
        if (stateMigrationAttempted.current) return;
        stateMigrationAttempted.current = true;
        const legacy = getLegacyMailboxPreferences();
        if (!legacy) return;
        void mailbox.importPreferences(legacy.archivedThreadKeys, legacy.readAtByThread)
            .then(() => {
                markLegacyMailboxPreferencesImported();
                void queryClient.invalidateQueries({ queryKey: MAILBOX_THREADS_QUERY_KEY });
                void queryClient.invalidateQueries({ queryKey: MAILBOX_SUMMARY_QUERY_KEY });
            })
            .catch((syncError) => console.error("Legacy mailbox import failed", syncError));
    }, [queryClient]);

    function persistThreadOperation(operation: MailboxStateOperation, threadKeys: string[]) {
        if (threadKeys.length === 0) return;
        const threadSnapshots = queryClient.getQueriesData<InfiniteData<MailboxThreadsResponse>>({
            queryKey: MAILBOX_THREADS_QUERY_KEY,
        });
        const summarySnapshot = queryClient.getQueryData<MailboxSummary>(MAILBOX_SUMMARY_QUERY_KEY);
        patchMailboxThreadState(queryClient, operation, threadKeys);
        const chunks = Array.from({ length: Math.ceil(threadKeys.length / 100) }, (_, index) =>
            threadKeys.slice(index * 100, index * 100 + 100),
        );
        void (async () => {
            let inboxUnread = summarySnapshot?.inboxUnread ?? 0;
            for (const chunk of chunks) {
                const result = await mailbox.updateThreads(operation, chunk);
                inboxUnread = result.inboxUnread;
            }
            setMailboxUnreadTotal(queryClient, inboxUnread);
        })()
            .catch((syncError) => {
                for (const [queryKey, data] of threadSnapshots) queryClient.setQueryData(queryKey, data);
                queryClient.setQueryData(MAILBOX_SUMMARY_QUERY_KEY, summarySnapshot);
                console.error("Mailbox state sync failed", syncError);
                toast.error("Couldn't save that change — it's been undone.");
            });
    }

    function persistThreadStates(states: Array<{
        threadKey: string;
        lastSeenAt?: string;
        archived?: boolean;
        trashed?: boolean;
        starred?: boolean;
    }>) {
        if (states.length === 0) return;
        const first = states[0];
        const operation: MailboxStateOperation = first.archived !== undefined
            ? (first.archived ? "archive" : "unarchive")
            : first.trashed !== undefined
                ? (first.trashed ? "trash" : "restore")
                : first.starred !== undefined
                    ? (first.starred ? "star" : "unstar")
                    : first.lastSeenAt === "1970-01-01T00:00:00.000Z"
                        ? "unread"
                        : "read";
        persistThreadOperation(operation, states.map(state => state.threadKey));
    }

    // The selected thread is the URL param — single source of truth.
    const selectedKey = urlThreadKey;
    useEffect(() => setFocusTwoExpanded(false), [activeDraftId, selectedKey]);
    function selectThread(k: string | null) {
        // The composer is intentionally single-instance now that the draft tab
        // shelf is gone. Moving back to a conversation closes that instance.
        setDrafts([]);
        setActiveDraftId(null);
        closeContext("mailbox-compose");
        navigate(k ? `${basePath}/${encodeURIComponent(k)}` : basePath);
    }

    const selectedMessagesQuery = useInfiniteQuery({
        ...threadMessagesQueryOptions(selectedKey ?? ""),
        enabled: Boolean(view && selectedKey),
    });
    const selectedViewMessages = useMemo(
        () => selectedMessagesQuery.data?.pages.flatMap(page => page.data) ?? [],
        [selectedMessagesQuery.data?.pages],
    );
    const { data: productData } = useQuery({
        queryKey: ["products"],
        queryFn:  () => productsApi.list(),
        staleTime: 5 * 60_000,
    });
    const { data: directoryContacts = [] } = useQuery({
        queryKey: CONTACTS_QUERY_KEY,
        queryFn: () => contactsApi.listAll(),
        ...directoryQueryOptions,
    });
    const contactsByEmail = useMemo(() => {
        const index = new Map<string, Contact>();
        for (const contact of directoryContacts) {
            const email = contact.email?.trim().toLowerCase();
            if (email) index.set(email, contact);
        }
        return index;
    }, [directoryContacts]);

    function openContact(address: string, fallbackName?: string | null, productKey: ProductKey | null = null) {
        const email = extractAddrs(address)[0]?.toLowerCase() ?? address.trim().toLowerCase();
        if (!email) return;
        setContactFocus({ email, fallbackName: usableName(fallbackName) ?? email, productKey });
    }
    function openThreadInContext(thread: Thread) {
        openContext(
            "mailbox-thread",
            <MailThreadContextPanel message={thread.latest} onClose={() => closeContext("mailbox-thread")} />,
            "email conversation",
        );
    }
    const selfAddresses = useMemo(() => new Set(
        (productData?.products ?? [])
            .map(product => product.fromAddress?.toLowerCase())
            .filter((address): address is string => Boolean(address)),
    ), [productData]);
    const mailboxError = serverMailboxQuery.error;
    const errorSource = mailboxError ?? selectedMessagesQuery.error;
    const error = errorSource
        ? String((errorSource as any).body?.detail ?? (errorSource as any).body?.title ?? (errorSource as Error).message)
        : null;

    // Close thread-specific actions when the thread switches. Draft editors
    // remain mounted so their in-progress state survives tab changes.
    useEffect(() => {
        setMeetingOpen(false);
    }, [selectedKey]);

    const serverThreadPages = useMemo<Thread[][]>(() => (serverMailboxQuery.data?.pages ?? []).map(serverPage =>
        serverPage.threads.map((thread) => {
            const latest = thread.latestMessage;
            const participants = [
                { address: latest.fromAddress.trim().toLowerCase(), name: usableName(latest.fromName) },
                ...extractAddrs(latest.toAddress).map(address => ({ address: address.toLowerCase(), name: null })),
            ].filter((person, index, all) =>
                person.address && !selfAddresses.has(person.address)
                && all.findIndex(candidate => candidate.address === person.address) === index,
            );
            return {
                threadKey: thread.threadKey,
                latest,
                count: thread.messageCount,
                hasInbound: Boolean(thread.latestInboundAt),
                participants,
                latestInboundTs: thread.latestInboundAt,
                unread: thread.unread,
                archived: thread.archived,
                starred: thread.starred,
                trashed: thread.trashed,
                labels: thread.labels,
            };
        }).filter(thread => show !== "outboundOnly" || statusFilter === "any" || (thread.latest.status ?? "").toLowerCase() === statusFilter)
            .sort((left, right) => {
                const newestFirst = tsOf(right.latest).localeCompare(tsOf(left.latest));
                if (mailSort === "newest") return newestFirst;
                if (mailSort === "oldest") return -newestFirst;
                if (mailSort === "subject") {
                    return (left.latest.subject ?? "").localeCompare(right.latest.subject ?? "", undefined, { sensitivity: "base" }) || newestFirst;
                }
                const counterparty = (thread: Thread) => show === "outboundOnly"
                    ? (thread.participants[0]?.name ?? thread.participants[0]?.address ?? thread.latest.toAddress)
                    : (usableName(thread.latest.fromName) ?? thread.latest.fromAddress);
                return counterparty(left).localeCompare(counterparty(right), undefined, { sensitivity: "base" }) || newestFirst;
            }),
    ), [mailSort, selfAddresses, serverMailboxQuery.data?.pages, show, statusFilter]);
    const serverThreads = useMemo(() => serverThreadPages.flat(), [serverThreadPages]);

    // Existing controls still use the local maps for immediate button state;
    // keep only the loaded server rows mirrored into those maps.
    useEffect(() => {
        setArchived(previous => {
            const next = { ...previous };
            for (const thread of serverThreads) {
                if (thread.archived) next[thread.threadKey] = true;
                else delete next[thread.threadKey];
            }
            return next;
        });
        setTrashed(previous => {
            const next = { ...previous };
            for (const thread of serverThreads) {
                if (thread.trashed) next[thread.threadKey] = true;
                else delete next[thread.threadKey];
            }
            return next;
        });
        setStarred(previous => {
            const next = { ...previous };
            for (const thread of serverThreads) {
                if (thread.starred) next[thread.threadKey] = true;
                else delete next[thread.threadKey];
            }
            return next;
        });
    }, [serverThreads]);

    const loadedPageCount = Math.max(1, serverThreadPages.length);
    const safePage = Math.min(page, loadedPageCount - 1);
    const threads = serverThreadPages[safePage] ?? [];
    const rowsBeforePage = serverThreadPages.slice(0, safePage).reduce((total, rows) => total + rows.length, 0);
    const selectedThread = selectedKey ? serverThreads.find(thread => thread.threadKey === selectedKey) : undefined;
    const selectedThreadUnread = selectedThread?.unread ?? null;

    // Warm the conversation cache for the 25 rows the user can currently see.
    // A small worker pool prevents a page change from issuing every history
    // request simultaneously; selecting a prefetched row reuses this exact key.
    useEffect(() => {
        const threadKeys = [...new Set(threads.map(thread => thread.threadKey))];
        let nextIndex = 0;
        let cancelled = false;

        const prefetchWorker = async () => {
            while (!cancelled) {
                const threadKey = threadKeys[nextIndex];
                nextIndex += 1;
                if (!threadKey) return;
                await queryClient.prefetchInfiniteQuery(threadMessagesQueryOptions(threadKey));
            }
        };

        const workerCount = Math.min(THREAD_PREFETCH_CONCURRENCY, threadKeys.length);
        void Promise.all(Array.from({ length: workerCount }, prefetchWorker));
        return () => { cancelled = true; };
    }, [queryClient, threads]);

    // A URL change is the single read transition. Wait for the thread row when
    // possible so already-read conversations produce no write at all.
    useEffect(() => {
        if (!selectedKey) {
            lastMarkedReadKey.current = null;
            return;
        }
        if (lastMarkedReadKey.current === selectedKey) return;
        if (selectedThreadUnread === null && serverMailboxQuery.isLoading) return;
        lastMarkedReadKey.current = selectedKey;
        if (selectedThreadUnread === false) return;
        const seenAt = new Date().toISOString();
        persistThreadStates([{ threadKey: selectedKey, lastSeenAt: seenAt }]);
        // persistThreadStates is intentionally stable for the mounted mailbox.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedKey, selectedThreadUnread, serverMailboxQuery.isLoading]);

    /** A thread is new when its latest inbound row is newer than the user's
     *  cross-device read pointer (with local cache for offline startup). */
    function isThreadNew(t: Thread): boolean {
        return t.unread;
    }

    const threadGroups = useMemo(() => {
        if (mailGroup === "none") return [{ key: "all", label: null, threads }];
        const groups = new Map<string, Thread[]>();
        const labelFor = (thread: Thread) => {
            if (mailGroup === "date") return dateGroupLabel(tsOf(thread.latest));
            if (mailGroup === "read") return isThreadNew(thread) ? "Unread" : "Read";
            if (mailGroup === "status") return STATUS_STYLES[thread.latest.status ?? ""]?.label ?? thread.latest.status ?? "Unknown";
            return show === "outboundOnly"
                ? (thread.participants[0]?.name ?? thread.participants[0]?.address ?? extractAddrs(thread.latest.toAddress)[0] ?? thread.latest.toAddress)
                : (usableName(thread.latest.fromName) ?? thread.latest.fromAddress);
        };
        for (const thread of threads) {
            const label = labelFor(thread);
            const group = groups.get(label);
            if (group) group.push(thread);
            else groups.set(label, [thread]);
        }
        return [...groups].map(([label, groupedThreads]) => ({ key: label, label, threads: groupedThreads }));
    }, [mailGroup, show, threads]);

    const unreadCount = useMailUnreadCount();
    const quickFilterTabs = useMemo(() => [
        { id: "all", label: "All" },
        ...(view === "inbox" ? [{ id: "unread", label: "Unread", count: unreadCount }] : []),
        ...(show === "inbound" ? [
            { id: "to-me", label: "To me" },
            { id: "cc-me", label: "CC me" },
        ] : []),
        ...(productData?.products ?? []).map(product => ({
            id: `product:${product.key}`,
            label: product.name,
        })),
    ], [productData?.products, show, unreadCount, view]);

    function markAllRead() {
        const keys = serverThreads.map((t) => t.threadKey);
        persistThreadOperation("read", keys);
    }

    async function showNextPage() {
        if (safePage < serverThreadPages.length - 1) {
            setPage(safePage + 1);
            return;
        }
        if (!serverMailboxQuery.hasNextPage) return;
        const result = await serverMailboxQuery.fetchNextPage();
        if ((result.data?.pages.length ?? 0) > safePage + 1) setPage(safePage + 1);
    }

    // -----------------------------------------------------------------------
    // Bulk selection
    // -----------------------------------------------------------------------

    /** Drop selection entries that no longer correspond to a visible thread —
     *  prevents an archived/disappeared thread from ghost-counting toward
     *  "N selected" forever. */
    useEffect(() => {
        if (selected.size === 0) return;
        const visible = new Set(serverThreads.map((t) => t.threadKey));
        let changed = false;
        const next = new Set<string>();
        for (const k of selected) {
            if (visible.has(k)) next.add(k);
            else changed = true;
        }
        if (changed) setSelected(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serverThreads]);

    function toggleSelected(k: string) {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(k)) next.delete(k); else next.add(k);
            return next;
        });
    }

    function clearSelection() {
        setSelected(new Set());
    }

    function selectAllVisible() {
        setSelected(new Set(serverThreads.map((t) => t.threadKey)));
    }

    /** Keep the preview pane moving after rows leave the current mailbox.
     *  Prefer the row rendered immediately below the removed one, then walk
     *  upward when the removal happened at the end of the current page. */
    function threadAfterRemoval(threadKey: string, removed = new Set([threadKey])) {
        const index = threads.findIndex(thread => thread.threadKey === threadKey);
        if (index < 0) return null;
        for (let candidate = index + 1; candidate < threads.length; candidate += 1) {
            if (!removed.has(threads[candidate].threadKey)) return threads[candidate];
        }
        for (let candidate = index - 1; candidate >= 0; candidate -= 1) {
            if (!removed.has(threads[candidate].threadKey)) return threads[candidate];
        }
        return null;
    }

    function navigateAfterRemoval(threadKey: string, removed?: Set<string>) {
        const nextThread = threadAfterRemoval(threadKey, removed);
        navigate(nextThread ? `${basePath}/${encodeURIComponent(nextThread.threadKey)}` : basePath);
    }

    function archiveSelected() {
        if (selected.size === 0) return;
        const keys = [...selected];
        const removed = new Set(keys);
        const restoring = view === "archive";
        if (restoring) {
            setArchived(previous => {
                const next = { ...previous };
                for (const key of keys) delete next[key];
                return next;
            });
        } else {
            setArchived(archiveThreads(keys));
        }
        persistThreadStates(keys.map((threadKey) => ({ threadKey, archived: !restoring })));
        clearSelection();
        if (selectedKey && removed.has(selectedKey)) navigateAfterRemoval(selectedKey, removed);
    }

    function trashSelected() {
        if (selected.size === 0) return;
        const keys = [...selected];
        const removed = new Set(keys);
        const restoring = view === "trash";
        setTrashed(previous => {
            const next = { ...previous };
            for (const key of keys) {
                if (restoring) delete next[key];
                else next[key] = true;
            }
            return next;
        });
        persistThreadStates(keys.map((threadKey) => ({ threadKey, trashed: !restoring })));
        clearSelection();
        if (selectedKey && removed.has(selectedKey)) navigateAfterRemoval(selectedKey, removed);
    }

    /** Optimistic label change: chips update now, roll back if the server refuses. */
    function applyThreadLabels(operation: "add" | "remove", threadKeys: string[], labelIds: string[]) {
        if (threadKeys.length === 0) return;
        const rollback = snapshotMailbox(queryClient);
        patchMailboxThreadLabels(queryClient, operation, threadKeys, labelIds);
        void mailbox.updateThreadLabels(operation, threadKeys, labelIds)
            .catch((syncError) => {
                rollback();
                console.error("Mailbox label sync failed", syncError);
                toast.error("Couldn't update labels — change undone.");
            });
    }

    function addLabelToSelected(labelId: string) {
        if (selected.size === 0) return;
        applyThreadLabels("add", [...selected], [labelId]);
        clearSelection();
    }

    function toggleLabelOnCurrent(labelId: string, applied: boolean) {
        if (!selectedKey) return;
        applyThreadLabels(applied ? "remove" : "add", [selectedKey], [labelId]);
    }

    function markSelectedRead() {
        if (selected.size === 0) return;
        const keys = [...selected];
        persistThreadOperation("read", keys);
        clearSelection();
    }

    function markUnread(threadKey: string) {
        persistThreadOperation("unread", [threadKey]);
    }

    const allVisibleSelected = serverThreads.length > 0 && serverThreads.every((t) => selected.has(t.threadKey));
    const someSelected = selected.size > 0 && !allVisibleSelected;

    function toggleArchiveCurrent() {
        if (!selectedKey) return;
        const isArchived = !!archived[selectedKey];
        if (isArchived) {
            // Restoring — stay on the thread so the user can see it's back.
            setArchived(unarchiveThread(selectedKey));
            persistThreadStates([{ threadKey: selectedKey, archived: false }]);
            return;
        }
        setArchived(archiveThread(selectedKey));
        persistThreadStates([{ threadKey: selectedKey, archived: true }]);
        navigateAfterRemoval(selectedKey);
    }

    function setThreadTrashed(threadKey: string, nextValue: boolean) {
        setTrashed(previous => {
            const next = { ...previous };
            if (nextValue) next[threadKey] = true;
            else delete next[threadKey];
            return next;
        });
        persistThreadStates([{ threadKey, trashed: nextValue }]);
    }

    function setThreadStarred(threadKey: string, nextValue: boolean) {
        setStarred(previous => {
            const next = { ...previous };
            if (nextValue) next[threadKey] = true;
            else delete next[threadKey];
            return next;
        });
        persistThreadStates([{ threadKey, starred: nextValue }]);
    }

    function toggleTrashCurrent() {
        if (!selectedKey) return;
        const restoring = Boolean(trashed[selectedKey]) || view === "trash";
        setThreadTrashed(selectedKey, !restoring);
        if (!restoring) navigateAfterRemoval(selectedKey);
    }

    function toggleStarCurrent() {
        if (!selectedKey) return;
        setThreadStarred(selectedKey, !starred[selectedKey]);
    }

    const conversation = useMemo<Message[]>(() => {
        if (!selectedKey) return [];
        return selectedViewMessages
            .filter((m) => m.threadKey === selectedKey)
            .sort((a, b) => b.createdAt.localeCompare(a.createdAt));   // newest first
    }, [selectedKey, selectedViewMessages]);

    const latestInThread = conversation[0] ?? null;                       // sorted newest-first
    const latestContact = latestInThread ? (latestInThread.direction === "inbound"
        ? {
            address: latestInThread.fromAddress,
            name: usableName(latestInThread.fromName),
            productKey: latestInThread.productKey,
        }
        : {
            address: selectedThread?.participants[0]?.address ?? extractAddrs(latestInThread.toAddress)[0] ?? latestInThread.toAddress,
            name: usableName(selectedThread?.participants[0]?.name),
            productKey: latestInThread.productKey,
        }) : null;
    const focusedContact = contactFocus ? contactsByEmail.get(contactFocus.email) : undefined;
    useEffect(() => {
        if (!contactFocus) {
            closeContext("mailbox-contact");
            return;
        }
        openContext(
            "mailbox-contact",
            <ContactContextPanel
                focus={contactFocus}
                contact={focusedContact}
                onClose={() => setContactFocus(null)}
            />,
            "contact details",
        );
    }, [closeContext, contactFocus, focusedContact, openContext]);
    useEffect(() => () => closeContext("mailbox-contact"), [closeContext]);
    // Everyone on the thread (from/to/cc/bcc across all messages, minus us),
    // shown as chips under the subject.
    const threadPeople = useMemo(() => {
        const byAddress = new Map<string, { address: string; name: string | null }>();
        for (const message of conversation) {
            const people = [
                { address: message.fromAddress.trim().toLowerCase(), name: usableName(message.fromName) },
                ...parsePeople(message.toAddress),
                ...parsePeople(message.ccAddress),
                ...parsePeople(message.bccAddress),
            ];
            for (const person of people) {
                if (!person.address || isSelfAddr(person.address, selfAddresses)) continue;
                const known = byAddress.get(person.address);
                if (!known) byAddress.set(person.address, person);
                else if (!known.name && person.name) known.name = person.name;
            }
        }
        return [...byAddress.values()];
    }, [conversation, selfAddresses]);

    const meetingContext = useMemo<MeetingPopoverContext | null>(() => {
        if (!latestInThread) return null;
        const attendees = new Set<string>();
        for (const message of conversation) {
            if (message.direction === "inbound" && !isSelfAddr(message.fromAddress, selfAddresses)) {
                attendees.add(message.fromAddress.toLowerCase());
            }
            for (const address of extractAddrs(message.toAddress)) {
                if (!isSelfAddr(address, selfAddresses)) attendees.add(address);
            }
        }
        const inbound = conversation.find(message => message.direction === "inbound");
        const participant = inbound?.fromName?.trim()
            || inbound?.fromAddress
            || attendees.values().next().value
            || "contact";
        const productName = (productData?.products ?? [])
            .find(product => product.key === latestInThread.productKey)?.name
            || (latestInThread.productKey === "attend" ? "Attend" : null)
            || (latestInThread.productKey === "live-room" ? "Live Room" : null)
            || "Kardoe";
        return {
            productKey: latestInThread.productKey,
            title: `${participant} & Paul | ${productName}`,
            attendees: [...attendees],
        };
    }, [conversation, latestInThread, productData, selfAddresses]);
    const meetingActivityQuery = useQuery({
        queryKey: ["meeting-activity", selectedKey, ...(meetingContext?.attendees ?? [])],
        queryFn: () => meetingsApi.activity(meetingContext!.attendees),
        enabled: show === "inbound" && Boolean(selectedKey) && Boolean(meetingContext?.attendees.length),
        staleTime: 60_000,
    });
    const meetingActivity = meetingActivityQuery.data?.activity ?? null;
    const activeDraft = drafts.find(draft => draft.id === activeDraftId) ?? null;

    function openDraft(ctx: ComposerCtx) {
        const id = crypto.randomUUID();
        if (ctx.mode === "new") {
            setDrafts([]);
            setActiveDraftId(null);
            setMeetingOpen(false);
            openContext(
                "mailbox-compose",
                <MailComposeContextPanel
                    key={id}
                    ctx={ctx}
                    onClose={() => closeContext("mailbox-compose")}
                />,
                "email composer",
            );
            return;
        }
        closeContext("mailbox-compose");
        setDrafts([{ id, ctx, title: ctx.subject }]);
        setActiveDraftId(id);
        setMeetingOpen(false);
    }

    useEffect(() => () => closeContext("mailbox-compose"), [closeContext]);

    useEffect(() => {
        const prefill = (location.state as { compose?: MailboxComposePrefill } | null)?.compose;
        if (!prefill || composeLocationKey.current === location.key) return;
        composeLocationKey.current = location.key;
        openDraft({
            mode: prefill.mode ?? (prefill.inReplyTo ? "reply" : "new"),
            to: prefill.to ?? "",
            cc: prefill.cc,
            bcc: prefill.bcc,
            subject: prefill.subject ?? "",
            inReplyTo: prefill.inReplyTo ?? undefined,
            productKey: prefill.productKey ?? (getSelectedProduct() || null),
            quotedLead: prefill.quotedLead ?? "",
            quotedBody: prefill.quotedBody ?? "",
            draftId: prefill.draftId,
            draftBodyHtml: prefill.draftBodyHtml,
            draftAttachments: prefill.draftAttachments,
        });
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [location.key, location.pathname, location.search, location.state, navigate]);

    function closeDraft(draftId: string) {
        setDrafts(previous => {
            const index = previous.findIndex(draft => draft.id === draftId);
            const next = previous.filter(draft => draft.id !== draftId);
            if (activeDraftId === draftId) setActiveDraftId(next[Math.min(index, next.length - 1)]?.id ?? null);
            return next;
        });
    }

    function openReply(target: Message) {
        const to = target.direction === "inbound"
            ? (target.fromName
                ? `"${target.fromName}" <${target.fromAddress}>`
                : target.fromAddress)
            : target.toAddress;
        openDraft({
            mode:       "reply",
            to,
            subject:    replySubject(target.subject),
            inReplyTo:  target.messageIdHeader ?? undefined,
            productKey: target.productKey,
            quotedLead: quotePreamble(target),
            quotedBody: quotedBodyOf(target),
        });
    }

    function openReplyAll(target: Message) {
        const sender = target.fromAddress.toLowerCase();
        const others = extractAddrs(target.toAddress)
            .filter((a) => a !== sender && !isSelfAddr(a, selfAddresses));
        const all = target.direction === "inbound" ? [sender, ...others] : others;
        const deduped = [...new Set(all)];
        const to = deduped.map((a) => {
            if (a === sender && target.fromName) return `"${target.fromName}" <${a}>`;
            return a;
        }).join(", ");
        // Carry the original Cc line into the reply's Cc (dropping ourselves
        // and the people already on the To line we just built).
        const toAddrs = new Set(deduped);
        const cc = extractAddrs(target.ccAddress ?? "")
            .filter((a) => !toAddrs.has(a) && !isSelfAddr(a, selfAddresses))
            .join(", ");
        openDraft({
            mode:       "replyAll",
            to,
            cc:         cc || undefined,
            subject:    replySubject(target.subject),
            inReplyTo:  target.messageIdHeader ?? undefined,
            productKey: target.productKey,
            quotedLead: quotePreamble(target),
            quotedBody: quotedBodyOf(target),
        });
    }

    /** Resume a saved draft row in the inline composer (same shape as Drafts → Resume). */
    function openSavedDraft(draft: Message) {
        openDraft({
            mode:             draft.inReplyTo ? "reply" : "new",
            to:               draft.toAddress,
            cc:               draft.ccAddress ?? undefined,
            bcc:              draft.bccAddress ?? undefined,
            subject:          draft.subject ?? "",
            inReplyTo:        draft.inReplyTo ?? undefined,
            productKey:       draft.productKey,
            quotedLead:       "",
            quotedBody:       "",
            draftId:          draft.id,
            draftBodyHtml:    draft.bodyHtml ?? undefined,
            draftAttachments: draft.draftAttachments ?? undefined,
        });
    }

    function openForward(target: Message) {
        const lead = [
            "---------- Forwarded message ----------",
            `From: ${target.fromName ? `${target.fromName} <${target.fromAddress}>` : target.fromAddress}`,
            `Date: ${fmtLocal(tsOf(target))}`,
            `Subject: ${target.subject ?? "(no subject)"}`,
            `To: ${target.toAddress}`,
        ].join("\n");
        openDraft({
            mode:       "forward",
            to:         "",
            subject:    forwardSubject(target.subject),
            productKey: target.productKey,
            quotedLead: lead,
            quotedBody: quotedBodyOf(target),
        });
    }

    // The master checkbox's "indeterminate" state isn't expressible as a
    // React prop — we set it imperatively via a ref each render.
    const masterRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (masterRef.current) masterRef.current.indeterminate = someSelected;
    }, [someSelected]);

    return (
        <div className="page">
            {error && <div className="error">{error}</div>}
            <FocusSplit secondaryOpen={Boolean(selectedKey || activeDraft)} secondaryExpanded={focusTwoExpanded} defaultPrimaryWidth={280} minPrimaryWidth={220} minSecondaryWidth={320} storageKey="crm.inbox.focus-one-width">
                <div className="list mailbox-index">
                    <ActionBar
                        value={quickFilter}
                        onValueChange={value => setQuickFilter(value as MailboxQuickFilter)}
                        ariaLabel={`${title} views`}
                        tabs={quickFilterTabs}
                        leading={selected.size > 0 ? <input ref={masterRef} type="checkbox" checked={allVisibleSelected} onChange={e => e.target.checked ? selectAllVisible() : clearSelection()} title={allVisibleSelected ? "Clear selection" : "Select all visible"} /> : undefined}
                        selection={{
                            count: selected.size,
                            onClear: clearSelection,
                            actions: <>
                                {view !== "trash" && <Button onClick={archiveSelected}>{view === "archive" ? "Move to inbox" : "Archive"}</Button>}
                                <Button onClick={trashSelected}>{view === "trash" ? "Restore" : "Trash"}</Button>
                                {show === "inbound" && view !== "trash" && <Button onClick={markSelectedRead}>Mark read</Button>}
                                {(labelsQuery.data?.labels.length ?? 0) > 0 && <Menu.Root>
                                    <Menu.Trigger render={<Button variant="secondary" />}>Label</Menu.Trigger>
                                    <Menu.Content align="start">
                                        {labelsQuery.data!.labels.map(label => <Menu.Item key={label.id} onClick={() => addLabelToSelected(label.id)}>{label.name}</Menu.Item>)}
                                    </Menu.Content>
                                </Menu.Root>}
                            </>,
                        }}
                        search={threadSearch}
                        onSearch={setThreadSearch}
                        searchPlaceholder="Search… from: to: subject: is:unread after:2026-09-01"
                        displayMode={tableDensity}
                        onDisplayMode={setTableDensity}
                        layoutMode={mailLayout}
                        onLayoutMode={setMailLayout}
                        sortMode={mailSort}
                        onSortMode={value => setMailSort(value as MailSort)}
                        sortOptions={[
                            { id: "newest", label: "Newest first" },
                            { id: "oldest", label: "Oldest first" },
                            { id: "counterparty", label: show === "outboundOnly" ? "Recipient" : "Sender" },
                            { id: "subject", label: "Subject" },
                        ]}
                        groupMode={mailGroup}
                        onGroupMode={value => setMailGroup(value as MailGroup)}
                        groupOptions={show === "outboundOnly"
                            ? [
                                { id: "none", label: "None" },
                                { id: "date", label: "Date" },
                                { id: "counterparty", label: "Recipient" },
                                { id: "status", label: "Delivery status" },
                            ]
                            : [
                                { id: "none", label: "None" },
                                { id: "date", label: "Date" },
                                { id: "counterparty", label: "Sender" },
                                { id: "read", label: "Read status" },
                            ]}
                        actions={<>
                            {show === "inbound" && (mailboxRequestView === "inbox" || mailboxRequestView === "unread") && <Button variant="secondary" size="sm" onClick={markAllRead} disabled={unreadCount === 0}>Mark loaded read</Button>}
                            {show === "outboundOnly" && <label className="mailbox-status-filter">Status<SelectBox value={statusFilter} onValueChange={value => setStatusFilter(value as StatusFilter)} className="w-auto" options={[
                                { value: "any", label: "All" }, { value: "delivered", label: "Delivered" }, { value: "sent", label: "Sent (not yet delivered)" }, { value: "queued", label: "Queued" }, { value: "bounced", label: "Bounced" }, { value: "complained", label: "Complaint" },
                            ]} /></label>}
                        </>}
                    />
                    <div className="mailbox-table-wrap">
                    {mailLayout === "list" ? <div className={`mail-list density-${tableDensity}`}>
                    {threads.length === 0 && <div className="muted mailbox-empty">{serverMailboxQuery.isLoading ? "Loading…" : emptyMsg}</div>}
                    {threadGroups.map(group => (
                        <section className="mail-thread-group" key={group.key} aria-label={group.label ?? undefined}>
                        {group.label && <div className="mail-group-heading"><span>{group.label}</span><small>{group.threads.length}</small></div>}
                        {group.threads.map((t) => {
                        const unread = isThreadNew(t);
                        const isSelected = selected.has(t.threadKey);
                        const contactAddress = show === "outboundOnly"
                            ? (t.participants[0]?.address ?? extractAddrs(t.latest.toAddress)[0] ?? t.latest.toAddress)
                            : t.latest.fromAddress;
                        const contactName = show === "outboundOnly"
                            ? usableName(t.participants[0]?.name)
                            : usableName(t.latest.fromName);
                        const senderLabel = show === "outboundOnly"
                            ? `To: ${contactName ?? contactAddress}`
                            : (contactName ?? contactAddress);
                        const avatarParticipants = (t.participants.length > 0
                            ? t.participants
                            : [{ address: contactAddress, name: contactName }]
                        ).slice(0, 3);
                        const snippet = previewText(t.latest);
                        return (
                            <ContextMenu.Root key={t.threadKey}>
                            <ContextMenu.Trigger render={
                                <div
                                    className={
                                        "mail-row"
                                        + (t.threadKey === selectedKey ? " selected" : "")
                                        + (unread ? " unread" : "")
                                        + (isSelected ? " checked" : "")
                                    }
                                    onClick={() => selectThread(t.threadKey)}
                                />
                            }>
                                {/* The avatar IS the select toggle (Outlook mobile style) —
                                    tapping anywhere else on the row opens the thread. */}
                                <button
                                    type="button"
                                    className={`mail-row-avatar${isSelected ? " is-selected" : ""}`}
                                    data-count={avatarParticipants.length}
                                    aria-pressed={isSelected}
                                    aria-label={`${isSelected ? "Deselect" : "Select"} thread ${t.latest.subject ?? "(no subject)"}`}
                                    title={isSelected ? "Deselect" : "Select"}
                                    onClick={(e) => { e.stopPropagation(); toggleSelected(t.threadKey); }}
                                >
                                    {avatarParticipants.map((person, index) => (
                                        <span
                                            key={person.address}
                                            className={`mail-row-avatar-tile${isSelected && index === 0 ? " is-selected" : ""}`}
                                            style={{ "--avatar-index": index } as React.CSSProperties}
                                            title={person.name ? `${person.name} <${person.address}>` : person.address}
                                        >
                                            {isSelected && index === 0
                                                ? <Check size={15} />
                                                : initialsOf(person.name ?? person.address)}
                                        </span>
                                    ))}
                                    <span className="mail-row-dot" aria-label={unread ? "Unread" : undefined} />
                                </button>
                                <span className="mail-row-main">
                                    <span className="mail-row-top">
                                        <span className="mail-row-sender">{senderLabel}</span>
                                        {t.starred && <Star className="mail-row-star" size={13} fill="currentColor" aria-label="Starred" />}
                                        {t.count > 1 && <span className="mail-row-count">{t.count}</span>}
                                        <span className="mail-row-meta">
                                            {show === "outboundOnly" && (t.latest.direction === "outbound"
                                                ? <DeliveryStatus message={t.latest} />
                                                : <StatusBadge status={t.latest.status} />)}
                                            {show !== "outboundOnly" && t.latest.status === "draft" && <StatusBadge status="draft" />}
                                            <span className="mail-row-date">{formatMailboxDate(tsOf(t.latest))}</span>
                                        </span>
                                    </span>
                                    <span className="mail-row-subject">{t.latest.subject ?? "(no subject)"}</span>
                                    {t.labels.length > 0 && <span className="mail-row-labels">{t.labels.map(label => <span key={label.id} className={`mail-label mail-label--${label.color}`}>{label.name}</span>)}</span>}
                                    {snippet && <span className="mail-row-preview">{snippet}</span>}
                                </span>
                            </ContextMenu.Trigger>
                            <ContextMenu.Content>
                                <ContextMenu.Group>
                                    <ContextMenu.GroupLabel className="mail-context-menu-title">{t.latest.subject ?? "(no subject)"}</ContextMenu.GroupLabel>
                                    <ContextMenu.Item onClick={() => selectThread(t.threadKey)}>Open conversation</ContextMenu.Item>
                                    <ContextMenu.Item onClick={() => openThreadInContext(t)}>Open in context panel</ContextMenu.Item>
                                    <ContextMenu.Item onClick={() => openContact(contactAddress, contactName, t.latest.productKey)}>Open contact details</ContextMenu.Item>
                                </ContextMenu.Group>
                                <ContextMenu.Separator />
                                <ContextMenu.Group>
                                    <ContextMenu.Item onClick={() => openReply(t.latest)}>Reply</ContextMenu.Item>
                                    <ContextMenu.Item onClick={() => openReplyAll(t.latest)}>Reply all</ContextMenu.Item>
                                    {show === "inbound" && <ContextMenu.Item onClick={() => markUnread(t.threadKey)}>Mark as unread</ContextMenu.Item>}
                                </ContextMenu.Group>
                                <ContextMenu.Separator />
                                <ContextMenu.Group>
                                    <ContextMenu.Item onClick={() => setThreadStarred(t.threadKey, !starred[t.threadKey])}>{starred[t.threadKey] ? "Remove star" : "Star"}</ContextMenu.Item>
                                    <ContextMenu.Item onClick={() => {
                                        const restoring = Boolean(archived[t.threadKey]);
                                        setArchived(restoring ? unarchiveThread(t.threadKey) : archiveThread(t.threadKey));
                                        persistThreadStates([{ threadKey: t.threadKey, archived: !restoring }]);
                                        if (!restoring && selectedKey === t.threadKey) navigateAfterRemoval(t.threadKey);
                                    }}>{archived[t.threadKey] ? "Restore to inbox" : "Archive"}</ContextMenu.Item>
                                    <ContextMenu.Item onClick={() => {
                                        const restoring = Boolean(trashed[t.threadKey]) || view === "trash";
                                        setThreadTrashed(t.threadKey, !restoring);
                                        if (!restoring && selectedKey === t.threadKey) navigateAfterRemoval(t.threadKey);
                                    }}>{trashed[t.threadKey] || view === "trash" ? "Restore from trash" : "Move to trash"}</ContextMenu.Item>
                                    {(labelsQuery.data?.labels ?? []).map(label => (
                                        <ContextMenu.Item key={label.id} onClick={() => applyThreadLabels("add", [t.threadKey], [label.id])}>Label: {label.name}</ContextMenu.Item>
                                    ))}
                                </ContextMenu.Group>
                            </ContextMenu.Content>
                            </ContextMenu.Root>
                        );
                        })}
                        </section>
                    ))}
                    </div> : (
                        <table className={`data-table mail-table density-${tableDensity}`}>
                            <thead>
                                <tr>
                                    <th className="mail-table-select" aria-label="Selection" />
                                    <th>{show === "outboundOnly" ? "Recipient" : "Sender"}</th>
                                    <th>Subject</th>
                                    <th>Preview</th>
                                    {show === "outboundOnly" && <th>Status</th>}
                                    <th className="mail-table-date">Date</th>
                                </tr>
                            </thead>
                            {threadGroups.map(group => (
                                <tbody className="mail-table-group" key={group.key}>
                                    {group.label && (
                                        <tr className="mail-table-group-heading">
                                            <th colSpan={show === "outboundOnly" ? 6 : 5}><span>{group.label}</span><small>{group.threads.length}</small></th>
                                        </tr>
                                    )}
                                    {group.threads.map(t => {
                                    const unread = isThreadNew(t);
                                    const isSelected = selected.has(t.threadKey);
                                    const contactAddress = show === "outboundOnly"
                                        ? (t.participants[0]?.address ?? extractAddrs(t.latest.toAddress)[0] ?? t.latest.toAddress)
                                        : t.latest.fromAddress;
                                    const contactName = show === "outboundOnly" ? usableName(t.participants[0]?.name) : usableName(t.latest.fromName);
                                    const senderLabel = show === "outboundOnly"
                                        ? contactAddress
                                        : (contactName ?? contactAddress);
                                    const snippet = previewText(t.latest);
                                    return (
                                        <ContextMenu.Root key={t.threadKey}>
                                            <ContextMenu.Trigger render={
                                                <tr
                                                    className={`${t.threadKey === selectedKey ? "selected " : ""}${unread ? "unread " : ""}${isSelected ? "checked" : ""}`}
                                                    onClick={() => selectThread(t.threadKey)}
                                                />
                                            }>
                                                <td className="mail-table-select">
                                                    <span className={`mail-table-unread-dot${unread ? " is-visible" : ""}`} aria-label={unread ? "Unread" : undefined} />
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        aria-label={`${isSelected ? "Deselect" : "Select"} thread ${t.latest.subject ?? "(no subject)"}`}
                                                        onClick={event => event.stopPropagation()}
                                                        onChange={() => toggleSelected(t.threadKey)}
                                                    />
                                                </td>
                                                <td className="cell-strong"><span className="cell-truncate">{senderLabel}</span></td>
                                                <td className={unread ? "cell-strong" : ""}><span className="cell-truncate">{t.starred ? "★ " : ""}{t.latest.subject ?? "(no subject)"}{t.count > 1 ? ` (${t.count})` : ""}</span></td>
                                                <td className="cell-muted"><span className="cell-truncate mail-table-preview">{snippet}</span></td>
                                                {show === "outboundOnly" && <td><DeliveryStatus message={t.latest} /></td>}
                                                <td className="cell-muted mail-table-date">{formatMailboxDate(tsOf(t.latest))}</td>
                                            </ContextMenu.Trigger>
                                            <ContextMenu.Content>
                                                <ContextMenu.Group>
                                                    <ContextMenu.GroupLabel className="mail-context-menu-title">{t.latest.subject ?? "(no subject)"}</ContextMenu.GroupLabel>
                                                    <ContextMenu.Item onClick={() => selectThread(t.threadKey)}>Open conversation</ContextMenu.Item>
                                                    <ContextMenu.Item onClick={() => openThreadInContext(t)}>Open in context panel</ContextMenu.Item>
                                                    <ContextMenu.Item onClick={() => openContact(contactAddress, contactName, t.latest.productKey)}>Open contact details</ContextMenu.Item>
                                                </ContextMenu.Group>
                                                <ContextMenu.Separator />
                                                <ContextMenu.Group>
                                                    <ContextMenu.Item onClick={() => openReply(t.latest)}>Reply</ContextMenu.Item>
                                                    <ContextMenu.Item onClick={() => openReplyAll(t.latest)}>Reply all</ContextMenu.Item>
                                                    {show === "inbound" && <ContextMenu.Item onClick={() => markUnread(t.threadKey)}>Mark as unread</ContextMenu.Item>}
                                                </ContextMenu.Group>
                                                <ContextMenu.Separator />
                                                <ContextMenu.Group>
                                                    <ContextMenu.Item onClick={() => setThreadStarred(t.threadKey, !starred[t.threadKey])}>{starred[t.threadKey] ? "Remove star" : "Star"}</ContextMenu.Item>
                                                    <ContextMenu.Item onClick={() => {
                                                        const restoring = Boolean(archived[t.threadKey]);
                                                        setArchived(restoring ? unarchiveThread(t.threadKey) : archiveThread(t.threadKey));
                                                        persistThreadStates([{ threadKey: t.threadKey, archived: !restoring }]);
                                                        if (!restoring && selectedKey === t.threadKey) navigateAfterRemoval(t.threadKey);
                                                    }}>{archived[t.threadKey] ? "Restore to inbox" : "Archive"}</ContextMenu.Item>
                                                    <ContextMenu.Item onClick={() => {
                                                        const restoring = Boolean(trashed[t.threadKey]) || view === "trash";
                                                        setThreadTrashed(t.threadKey, !restoring);
                                                        if (!restoring && selectedKey === t.threadKey) navigateAfterRemoval(t.threadKey);
                                                    }}>{trashed[t.threadKey] || view === "trash" ? "Restore from trash" : "Move to trash"}</ContextMenu.Item>
                                                </ContextMenu.Group>
                                            </ContextMenu.Content>
                                        </ContextMenu.Root>
                                    );
                                    })}
                                </tbody>
                            ))}
                        </table>
                    )}
                    {mailLayout === "table" && threads.length === 0 && <div className="muted mailbox-empty">{serverMailboxQuery.isLoading ? "Loading…" : emptyMsg}</div>}
                    </div>
                    <PaginationFooter
                        totalRows={serverThreads.length}
                        page={safePage}
                        pageSize={MAILBOX_PAGE_SIZE}
                        rangeStart={threads.length === 0 ? 0 : rowsBeforePage + 1}
                        rangeEnd={rowsBeforePage + threads.length}
                        hasPrevious={safePage > 0}
                        hasNext={safePage < serverThreadPages.length - 1 || Boolean(serverMailboxQuery.hasNextPage)}
                        totalIsExact={serverMailboxQuery.hasNextPage !== true}
                        isLoadingNext={serverMailboxQuery.isFetchingNextPage}
                        onPrevious={() => setPage(current => Math.max(0, current - 1))}
                        onNext={() => void showNextPage()}
                    />
                </div>

                <div className={`list mailbox-conversation${activeDraft ? " is-composing" : ""}`}>
                    {!activeDraft && selectedKey && latestInThread && (
                        <>
                            {/* Slim sticky command bar — the ONLY sticky layer.
                                Toolbar sits above the subject; subject and sender scroll away with the content,
                                Outlook/Apple Mail style. */}
                            <div className="thread-toolbar">
                                {/* Mobile-only back chevron; CSS hides it on desktop. */}
                                <button
                                    className="back-to-list"
                                    onClick={() => navigate(basePath)}
                                    aria-label="Back to list"
                                    title="Back to list"
                                >
                                    ←
                                </button>
                                {latestContact && <IconAction
                                    label="Contact details"
                                    onClick={() => openContact(latestContact.address, latestContact.name, latestContact.productKey)}
                                ><UserRound /></IconAction>}
                                {show === "inbound" && meetingContext && (
                                    <IconAction label="Schedule meeting" onClick={() => { setActiveDraftId(null); setMeetingOpen(value => !value); }}>
                                        <CalendarPlus />
                                    </IconAction>
                                )}
                                {(labelsQuery.data?.labels.length ?? 0) > 0 && <Menu.Root>
                                    <Menu.Trigger render={<Button variant="ghost" size="sm" className="conversation-action" aria-label="Labels" title="Labels" />}><Tag /></Menu.Trigger>
                                    <Menu.Content align="start">
                                        {labelsQuery.data!.labels.map(label => {
                                            const applied = selectedThread?.labels.some(l => l.id === label.id) ?? false;
                                            return <Menu.Item key={label.id} className="mail-label-menu-item" onClick={() => toggleLabelOnCurrent(label.id, applied)}>
                                                <span className={`mail-label mail-label--${label.color}`}>{label.name}</span>
                                                {applied && <Check size={14} />}
                                            </Menu.Item>;
                                        })}
                                    </Menu.Content>
                                </Menu.Root>}
                                <IconAction
                                    label={archived[selectedKey!] ? "Restore to inbox" : "Archive"}
                                    onClick={toggleArchiveCurrent}
                                >
                                    {archived[selectedKey!] ? <ArchiveRestore /> : <Archive />}
                                </IconAction>
                                <IconAction label={starred[selectedKey!] ? "Remove star" : "Star"} onClick={toggleStarCurrent}>
                                    <Star fill={starred[selectedKey!] ? "currentColor" : "none"} />
                                </IconAction>
                                <IconAction label={trashed[selectedKey!] || view === "trash" ? "Restore from trash" : "Move to trash"} onClick={toggleTrashCurrent}>
                                    {trashed[selectedKey!] || view === "trash" ? <ArchiveRestore /> : <Trash2 />}
                                </IconAction>
                                <span className="spacer" />
                                <FocusExpandButton
                                    className="conversation-expand"
                                    expanded={focusTwoExpanded}
                                    onToggle={() => setFocusTwoExpanded(value => !value)}
                                    label="conversation"
                                />
                                <Button variant="ghost" size="sm" className="conversation-close" onClick={() => navigate(basePath)} aria-label="Close conversation" title="Close conversation"><X /></Button>
                            </div>
                            <div className="thread-intro">
                                <h2 className="conversation-subject">{latestInThread.subject ?? "(no subject)"}</h2>
                            </div>
                            {threadPeople.length > 0 && <div className="thread-people">
                                {threadPeople.map(person => (
                                    <MailPerson
                                        key={person.address}
                                        address={person.address}
                                        name={person.name}
                                        contactId={contactsByEmail.get(person.address)?.id}
                                        onOpen={() => openContact(person.address, person.name, latestInThread.productKey)}
                                    />
                                ))}
                            </div>}
                            <div className="thread-links"><LinkedItems type="thread" id={selectedKey} compact /></div>

                        </>
                    )}

                    {!activeDraft && selectedKey && meetingActivity && (
                        <div className={`thread-activity${meetingActivity.responseStatus === "cancelled" ? " thread-activity--cancelled" : ""}`}>
                            <strong>{meetingActivityLabel(meetingActivity)}</strong>
                            <span className="thread-activity-date">{meetingActivityDate(meetingActivity)}</span>
                            <span className="spacer" />
                            {meetingActivity.meetUrl && meetingActivity.responseStatus !== "cancelled" && (
                                <a href={meetingActivity.meetUrl} target="_blank" rel="noreferrer">
                                    Join
                                </a>
                            )}
                        </div>
                    )}

                    {!activeDraft && selectedKey && meetingOpen && meetingContext && (
                        <MeetingPopover
                            key={selectedKey}
                            context={meetingContext}
                            onCancel={() => setMeetingOpen(false)}
                        />
                    )}

                    {activeDraft && <div className="mail-draft-panels">
                        {drafts.map(draft => (
                            <div key={draft.id} className="mail-draft-panel" hidden={draft.id !== activeDraftId}>
                                <InlineComposer
                                    ctx={draft.ctx}
                                    onSubjectChange={title => setDrafts(previous => previous.map(item => item.id === draft.id ? { ...item, title } : item))}
                                    onCancel={() => closeDraft(draft.id)}
                                    onSent={() => closeDraft(draft.id)}
                                />
                            </div>
                        ))}
                    </div>}

                    {!activeDraft && selectedKey && <ConversationMessages
                        key={selectedKey}
                        conversation={conversation}
                        hasOlder={selectedMessagesQuery.hasNextPage}
                        loadingOlder={selectedMessagesQuery.isFetchingNextPage}
                        onLoadOlder={() => void selectedMessagesQuery.fetchNextPage()}
                        onReply={openReply}
                        onReplyAll={openReplyAll}
                        onForward={openForward}
                        onEditDraft={openSavedDraft}
                        onOpenPerson={openContact}
                        contactIdFor={(address) => contactsByEmail.get(address)?.id}
                    />}
                </div>
            </FocusSplit>
        </div>
    );
}

/**
 * A person in a thread — click opens the contact rail, right-click gives the
 * full record, or the name/address on the clipboard.
 */
function MailPerson({ address, name, contactId, onOpen, className }: {
    address: string;
    name: string | null;
    contactId?: string;
    onOpen: () => void;
    className?: string;
}) {
    const label = name ?? address;
    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger render={
                <button
                    type="button"
                    className={className ?? "mail-person"}
                    title={name ? `${name} <${address}>` : address}
                    onClick={onOpen}
                />
            }>
                {label}
            </ContextMenu.Trigger>
            <MailPersonMenu address={address} name={name} contactId={contactId} onOpen={onOpen} />
        </ContextMenu.Root>
    );
}

/**
 * The sender name in a message card's accordion header. It sits inside the
 * accordion trigger (a <button>), so it can't be a button itself — left-click
 * still toggles the card; right-click gets the same person menu as To/Cc.
 */
function MailSender({ address, name, label, contactId, onOpen }: {
    address: string;
    name: string | null;
    label: string;
    contactId?: string;
    onOpen: () => void;
}) {
    return (
        <ContextMenu.Root>
            <ContextMenu.Trigger render={<strong title={name ? `${name} <${address}>` : address} />}>
                {label}
            </ContextMenu.Trigger>
            <MailPersonMenu address={address} name={name} contactId={contactId} onOpen={onOpen} />
        </ContextMenu.Root>
    );
}

function MailPersonMenu({ address, name, contactId, onOpen }: {
    address: string;
    name: string | null;
    contactId?: string;
    onOpen: () => void;
}) {
    const navigate = useNavigate();
    const label = name ?? address;
    const copy = (text: string) => { void navigator.clipboard.writeText(text); toast.success("Copied"); };
    return (
        <ContextMenu.Content>
            <ContextMenu.Group>
                <ContextMenu.GroupLabel className="mail-context-menu-title">{label}</ContextMenu.GroupLabel>
                <ContextMenu.Item onClick={onOpen}>Open contact details</ContextMenu.Item>
                <ContextMenu.Item onClick={() => navigate(contactId ? `/contacts/${contactId}` : `/contacts?q=${encodeURIComponent(address)}`)}>Open in full window</ContextMenu.Item>
            </ContextMenu.Group>
            <ContextMenu.Separator />
            <ContextMenu.Group>
                <ContextMenu.Item onClick={() => copy(name ? `${name} <${address}>` : address)}>Copy name and email</ContextMenu.Item>
                <ContextMenu.Item onClick={() => copy(address)}>Copy email address</ContextMenu.Item>
            </ContextMenu.Group>
        </ContextMenu.Content>
    );
}

/** A To/Cc/Bcc header rendered as clickable people. */
function PeopleLine({ header, onOpenPerson, contactIdFor }: {
    header: string;
    onOpenPerson: (address: string, name: string | null) => void;
    contactIdFor: (address: string) => string | undefined;
}) {
    const people = parsePeople(header);
    if (people.length === 0) return <span className="message-meta-value">{header}</span>;
    return (
        <span className="message-meta-value">
            {people.map((person, index) => (
                <Fragment key={person.address}>
                    {index > 0 && ", "}
                    <MailPerson
                        address={person.address}
                        name={person.name}
                        contactId={contactIdFor(person.address)}
                        onOpen={() => onOpenPerson(person.address, person.name)}
                        className="mail-person mail-person--inline"
                    />
                </Fragment>
            ))}
        </span>
    );
}

function ConversationMessages({
    conversation,
    hasOlder,
    loadingOlder,
    onLoadOlder,
    onReply,
    onReplyAll,
    onForward,
    onEditDraft,
    onOpenPerson,
    contactIdFor,
}: {
    conversation: Message[];
    hasOlder: boolean;
    loadingOlder: boolean;
    onLoadOlder: () => void;
    onReply: (message: Message) => void;
    onReplyAll: (message: Message) => void;
    onForward: (message: Message) => void;
    onEditDraft: (message: Message) => void;
    onOpenPerson: (address: string, name: string | null, productKey: ProductKey | null) => void;
    contactIdFor: (address: string) => string | undefined;
}) {
    const newestId = conversation[0]?.id;
    const [expandedIds, setExpandedIds] = useState<string[]>(() => newestId ? [newestId] : []);

    // Realtime can prepend a newer message while the thread is open. Make the
    // new first message the sole initially-open item, matching a fresh visit.
    useEffect(() => {
        setExpandedIds(newestId ? [newestId] : []);
    }, [newestId]);

    return (
        <>
        <Accordion.Root
            className="conversation-timeline"
            value={expandedIds}
            onValueChange={value => setExpandedIds(value.filter((id): id is string => typeof id === "string"))}
            multiple
        >
            {conversation.map((message) => (
                <Accordion.Item
                    key={message.id}
                    value={message.id}
                    className={`message-card message-card--${message.direction}${message.status === "draft" ? " message-card--draft" : ""}`}
                >
                    <Accordion.Header>
                        <Accordion.Trigger className="message-card-trigger">
                            <span className={`message-avatar${message.direction === "outbound" ? " is-me" : ""}`} aria-hidden="true">
                                {message.direction === "outbound"
                                    ? "Me"
                                    : initialsOf(usableName(message.fromName) ?? message.fromAddress)}
                            </span>
                            <span className="message-card-summary">
                                <span className="row">
                                    <MailSender
                                        address={message.fromAddress}
                                        name={usableName(message.fromName)}
                                        label={message.direction === "outbound" ? "Me" : (usableName(message.fromName) ?? message.fromAddress)}
                                        contactId={contactIdFor(message.fromAddress.trim().toLowerCase())}
                                        onOpen={() => onOpenPerson(message.fromAddress, usableName(message.fromName), message.productKey)}
                                    />
                                    {message.direction === "outbound" && <DeliveryStatus message={message} detailed />}
                                    <span className="spacer" />
                                    <span className="muted message-card-date">{formatMailboxDate(tsOf(message))}</span>
                                </span>
                                <span className="message-snippet">{previewText(message)}</span>
                            </span>
                        </Accordion.Trigger>
                    </Accordion.Header>
                    <Accordion.Panel className="message-card-panel">
                        <div className="message-card-panel-inner">
                            <div className="message-meta">
                                <span className="message-meta-label">To</span>
                                <PeopleLine header={message.toAddress} onOpenPerson={(address, name) => onOpenPerson(address, name, message.productKey)} contactIdFor={contactIdFor} />
                                {message.ccAddress && <>
                                    <span className="message-meta-label">Cc</span>
                                    <PeopleLine header={message.ccAddress} onOpenPerson={(address, name) => onOpenPerson(address, name, message.productKey)} contactIdFor={contactIdFor} />
                                </>}
                                {message.bccAddress && <>
                                    <span className="message-meta-label">Bcc</span>
                                    <PeopleLine header={message.bccAddress} onOpenPerson={(address, name) => onOpenPerson(address, name, message.productKey)} contactIdFor={contactIdFor} />
                                </>}
                                <span className="message-meta-label">Date</span>
                                <span className="message-meta-value muted">{formatFullDateTime(tsOf(message))}</span>
                            </div>
                            {/* Message-level actions live on the message; the thread toolbar only holds thread-wide ones. */}
                            {message.status === "draft" ? (
                                <div className="message-actions message-actions--draft">
                                    <Button size="sm" onClick={() => onEditDraft(message)}><Pencil /> Edit draft</Button>
                                    <span className="muted">This message hasn't been sent.</span>
                                </div>
                            ) : (
                                <div className="message-actions">
                                    <IconAction label="Reply" onClick={() => onReply(message)}><Reply /></IconAction>
                                    <IconAction label="Reply all" onClick={() => onReplyAll(message)}><ReplyAll /></IconAction>
                                    <IconAction label="Forward" onClick={() => onForward(message)}><Forward /></IconAction>
                                    <LinkedItems type="message" id={message.id} compact />
                                </div>
                            )}
                            {message.calendarInvite && <InviteCard message={message} />}
                            <MessageBody message={message} />
                        </div>
                    </Accordion.Panel>
                </Accordion.Item>
            ))}
        </Accordion.Root>
        {hasOlder && <div className="mailbox-load-more">
            <Button variant="secondary" onClick={onLoadOlder} disabled={loadingOlder}>
                {loadingOlder ? "Loading…" : "Load older messages"}
            </Button>
        </div>}
        </>
    );
}

/** Message body with the quoted history trimmed behind a Gmail-style "⋯"
 *  toggle. Detection is heuristic (see splitQuotedHtml/-Text above). */
function MessageBody({ message }: { message: Message }) {
    const [showQuoted, setShowQuoted] = useState(false);
    const parts = useMemo(() => {
        if (message.bodyHtml) {
            const resolved = resolveInlineImages(message.bodyHtml, message.inlineAssets);
            return { kind: "html" as const, ...splitQuotedHtml(sanitizeEmailHtml(resolved, { allowStyle: true })) };
        }
        return { kind: "text" as const, ...splitQuotedText(message.bodyText ?? "") };
    }, [message]);
    return (
        <>
            {parts.kind === "html"
                ? <div className="message-body message-body--html"><EmailFrame html={parts.main} /></div>
                : <pre className="message-body" style={{ whiteSpace: "pre-wrap" }}>{parts.main}</pre>}
            {message.direction === "inbound" && <ReceivedAttachments key={message.id} messageId={message.id} />}
            {parts.quoted && (
                <>
                    <button
                        type="button"
                        className="message-quote-toggle"
                        onClick={() => setShowQuoted(value => !value)}
                        aria-expanded={showQuoted}
                        title={showQuoted ? "Hide quoted text" : "Show quoted text"}
                        aria-label={showQuoted ? "Hide quoted text" : "Show quoted text"}
                    >
                        •••
                    </button>
                    {showQuoted && (parts.kind === "html"
                        ? <div className="message-body message-body--html message-body--quoted"><EmailFrame html={parts.quoted} title="Quoted email content" /></div>
                        : <pre className="message-body message-body--quoted" style={{ whiteSpace: "pre-wrap" }}>{parts.quoted}</pre>)}
                </>
            )}
        </>
    );
}

export function MailThreadContextPanel({ message, onClose }: { message: Message; onClose: () => void }) {
    const correspondent = message.direction === "inbound"
        ? (usableName(message.fromName) ?? message.fromAddress)
        : message.toAddress;
    return (
        <aside className="context-rail mail-thread-context-panel" aria-label="Email conversation">
            <div className="contact-context-controls">
                <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close email context" title="Close"><X /></Button>
            </div>
            <header>
                <span>{message.direction === "inbound" ? "From" : "To"} {correspondent}</span>
                <h2>{message.subject ?? "(no subject)"}</h2>
                <time>{formatFullDateTime(tsOf(message))}</time>
            </header>
            <div className="mail-thread-context-body">
                {message.calendarInvite && <InviteCard message={message} />}
                <MessageBody message={message} />
            </div>
        </aside>
    );
}

function MailComposeContextPanel({ ctx, onClose }: { ctx: ComposerCtx; onClose: () => void }) {
    return (
        <aside className="context-rail mail-compose-context-panel" aria-label="New email">
            <InlineComposer ctx={ctx} onCancel={onClose} onSent={onClose} />
        </aside>
    );
}

export function ContactContextPanel({
    focus,
    contact,
    onClose,
}: {
    focus: ContactFocus;
    contact?: Contact;
    onClose: () => void;
}) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [createdLead, setCreatedLead] = useState<Lead | null>(null);
    const [createdCompany, setCreatedCompany] = useState<Company | null>(null);
    const name = contactDisplayName(contact, focus.fallbackName, focus.email);
    const initials = initialsOf(name);
    const productKey = (focus.productKey ?? getSelectedProduct()) || null;
    const companySuggestion = inferredCompanyName(focus.email);
    const recipient = contact?.email
        ? (usableName(contact.name) ? `"${contact.name!.trim()}" <${contact.email}>` : contact.email)
        : focus.email;
    const location = [contact?.city, contact?.state, contact?.country].filter(Boolean).join(", ");
    const hasDetails = Boolean(contact?.phone || contact?.role || contact?.productKey || location);

    type QuickCrmResult = {
        kind: "contact" | "lead" | "company";
        contact: Contact;
        lead?: Lead;
        company?: Company;
        reused?: boolean;
    };

    const quickCreate = useMutation({
        mutationFn: async (kind: QuickCrmResult["kind"]): Promise<QuickCrmResult> => {
            const ensureContact = async (companyId?: string): Promise<Contact> => {
                if (contact) {
                    if (companyId && !contact.companyId) return contactsApi.update(contact.id, { companyId });
                    return contact;
                }
                const parts = splitPersonName(focus.fallbackName === focus.email ? "" : focus.fallbackName);
                const result = await contactsApi.save({
                    productKey,
                    eventId: null,
                    companyId: companyId ?? null,
                    firstName: parts.firstName,
                    lastName: parts.lastName,
                    email: focus.email,
                    phone: null,
                    jobTitle: null,
                    role: null,
                    isPrimary: true,
                });
                if (!result.ok || !result.contact) throw new Error(result.error || "Could not create contact.");
                return result.contact;
            };

            if (kind === "contact") return { kind, contact: await ensureContact() };
            if (kind === "lead") {
                const person = await ensureContact();
                const existing = await leadsApi.list({ contactId: person.id, limit: "20" });
                const lead = existing.data.find(row => row.stage !== "lost") ?? await leadsApi.create({
                    title: name,
                    stage: "new",
                    contactId: person.id,
                    ...(productKey ? { productKey } : {}),
                    source: "email",
                    notes: `Created from email correspondence with ${focus.email}.`,
                });
                return { kind, contact: person, lead, reused: existing.data.some(row => row.id === lead.id) };
            }

            if (!companySuggestion) throw new Error("This address does not have a business domain to use as a company.");
            const matches = await companiesApi.list({ search: companySuggestion, limit: "100" });
            const existingCompany = matches.data.find(company => company.name.trim().toLowerCase() === companySuggestion.toLowerCase());
            const company = existingCompany ?? await companiesApi.create({
                name: companySuggestion,
                notes: `Created from email correspondence with ${focus.email}.`,
            });
            const person = await ensureContact(company.id);
            return { kind, contact: person, company, reused: Boolean(existingCompany) };
        },
        onSuccess: result => {
            upsertDirectoryRecord(queryClient, "contacts", CONTACTS_QUERY_KEY, result.contact);
            if (result.lead) {
                upsertDirectoryRecord(queryClient, "leads", LEADS_QUERY_KEY, result.lead);
                setCreatedLead(result.lead);
            }
            if (result.company) {
                upsertDirectoryRecord(queryClient, "companies", COMPANIES_QUERY_KEY, result.company);
                setCreatedCompany(result.company);
            }
            const label = result.kind === "contact" ? "Contact"
                : result.kind === "lead" ? "Lead" : "Company";
            toast.success(`${label} ${result.reused ? "already exists" : "created"}`);
        },
        onError: error => toast.error(String((error as any).body?.detail ?? (error as Error).message)),
    });

    return (
        <aside className="context-rail contact-context-panel" aria-label={`${name} contact details`}>
            <div className="contact-context-controls">
                <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close contact" title="Close contact"><X /></Button>
            </div>

            <header className="contact-context-hero">
                <div className="contact-context-avatar" aria-hidden="true">{initials}</div>
                <div>
                    <h2>{name}</h2>
                    <a href={`mailto:${focus.email}`}>{focus.email}</a>
                    <div className="contact-context-meta">
                        {contact?.jobTitle && <span>{contact.jobTitle}</span>}
                        {contact?.company_label && <span>{contact.company_label}</span>}
                    </div>
                </div>
            </header>

            <div className="contact-context-actions">
                <button className="primary" onClick={() => navigate("/inbox", { state: { compose: { to: recipient, productKey: contact?.productKey ?? undefined } } })}><Mail />Email</button>
                <button onClick={() => navigate("/meetings", { state: { guests: recipient, productKey: contact?.productKey ?? undefined } })}><CalendarPlus />Schedule</button>
                {contact
                    ? <button onClick={() => navigate(`/contacts/${contact.id}`)}><ExternalLink />Full record</button>
                    : <button onClick={() => quickCreate.mutate("contact")} disabled={quickCreate.isPending}><UserPlus />Add contact</button>}
            </div>

            <div className="contact-context-body">
                {contact && <div className="contact-context-card"><LinkedItems type="contact" id={contact.id} /></div>}
                {!contact && (
                    <div className="contact-context-card contact-context-empty">
                        <strong>Not in Contacts yet.</strong>
                        <span>Create a CRM record directly from this email.</span>
                    </div>
                )}
                <div className="contact-context-card contact-context-quick-create">
                    <strong>CRM shortcuts</strong>
                    <div>
                        <button onClick={() => quickCreate.mutate("contact")} disabled={quickCreate.isPending || Boolean(contact)}><UserPlus />{contact ? "Contact added" : "Contact"}</button>
                        <button onClick={() => quickCreate.mutate("lead")} disabled={quickCreate.isPending}><Target />{createdLead ? "Lead ready" : "Lead"}</button>
                        <button onClick={() => quickCreate.mutate("company")} disabled={quickCreate.isPending || !companySuggestion || Boolean(contact?.companyId)} title={companySuggestion ? `Use ${companySuggestion}` : "No business domain detected"}><Building2 />{createdCompany ? "Company ready" : "Company"}</button>
                    </div>
                    {(createdLead || createdCompany) && <div className="contact-context-created-links">
                        {createdLead && <button onClick={() => navigate(`/pipeline/${createdLead.id}`)}>Open lead <ExternalLink /></button>}
                        {createdCompany && <button onClick={() => navigate("/companies")}>Open company <ExternalLink /></button>}
                    </div>}
                </div>
                {contact && hasDetails && (
                        <div className="contact-context-card">
                            <dl>
                                {contact.phone && <><dt>Phone</dt><dd>{contact.phone}</dd></>}
                                {contact.role && <><dt>Role</dt><dd>{contact.role}</dd></>}
                                {contact.productKey && <><dt>Product</dt><dd>{contact.productKey === "live-room" ? "Live Room" : "Attend"}</dd></>}
                                {location && <><dt>Location</dt><dd>{location}</dd></>}
                            </dl>
                        </div>
                )}
            </div>
        </aside>
    );
}

interface MeetingPopoverProps {
    context: MeetingPopoverContext;
    onCancel: () => void;
}

function localMeetingInput(date: Date): string {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60_000);
    return local.toISOString().slice(0, 16);
}

function moveMeetingStart(startAt: string, previousStartAt: string, previousEndAt: string): {
    startAt: string;
    endAt: string;
} {
    const previousDuration = new Date(previousEndAt).getTime() - new Date(previousStartAt).getTime();
    const duration = previousDuration > 0 ? previousDuration : 30 * 60_000;
    return {
        startAt,
        endAt: localMeetingInput(new Date(new Date(startAt).getTime() + duration)),
    };
}

function meetingError(value: unknown): string {
    if (value instanceof Error) {
        const body = (value as Error & { body?: any }).body;
        return body?.details || body?.error?.message || body?.error || value.message;
    }
    return String(value);
}

function MeetingPopover({ context, onCancel }: MeetingPopoverProps) {
    const queryClient = useQueryClient();
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(Math.ceil(now.getMinutes() / 30) * 30);
    const defaultStart = new Date(now.getTime() + 30 * 60_000);
    const defaultEnd = new Date(defaultStart.getTime() + 30 * 60_000);

    const [productKey, setProductKey] = useState<ProductKey | "">(
        context.productKey ?? getSelectedProduct(),
    );
    const [title, setTitle] = useState(context.title);
    const [startAt, setStartAt] = useState(() => localMeetingInput(defaultStart));
    const [endAt, setEndAt] = useState(() => localMeetingInput(defaultEnd));
    const [timeZone, setTimeZone] = useState(
        () => Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
    );
    const [attendees, setAttendees] = useState(context.attendees.join(", "));
    const [description, setDescription] = useState("");
    const [scheduled, setScheduled] = useState<Meeting | null>(null);
    const [invitesFailed, setInvitesFailed] = useState<{ email: string; error: string }[]>([]);

    const schedule = useMutation({
        mutationFn: async () => {
            const result = await meetingsApi.schedule({
                productKey: productKey || null,
                title: title.trim(),
                description: description.trim() || null,
                location: null,
                startAt: new Date(startAt).toISOString(),
                endAt: new Date(endAt).toISOString(),
                timeZone: timeZone.trim(),
                attendeeEmails: extractAddrs(attendees),
            });
            if (!result.ok || !result.meeting) {
                throw new Error(result.details || result.error || "Meeting could not be scheduled");
            }
            return result;
        },
        onSuccess: async result => {
            setScheduled(result.meeting!);
            setInvitesFailed(result.invitesFailed ?? []);
            await queryClient.invalidateQueries({ queryKey: ["meetings"] });
            await queryClient.invalidateQueries({ queryKey: ["meeting-activity"] });
        },
    });

    const validDates = Boolean(startAt && endAt && new Date(endAt) > new Date(startAt));
    const attendeeList = extractAddrs(attendees);

    return (
        <div className="meeting-popover">
            <div className="inline-composer-head">
                <strong>Schedule a meeting</strong>
                <span className="spacer" />
                <button onClick={onCancel} disabled={schedule.isPending}>Close</button>
            </div>

            {scheduled ? (
                <div>
                    <div className="meeting-banner">
                        Meeting scheduled. A calendar invite (.ics) was emailed to {scheduled.attendeeEmails.join(", ")}.
                    </div>
                    {invitesFailed.length > 0 && (
                        <div className="error meeting-banner">
                            Invite failed for {invitesFailed.map(f => `${f.email} (${f.error})`).join(", ")}.
                        </div>
                    )}
                    <div className="row" style={{ marginTop: 10, flexWrap: "wrap" }}>
                        {scheduled.meetUrl && (
                            <a href={scheduled.meetUrl} target="_blank" rel="noreferrer">Join call</a>
                        )}
                        <span className="spacer" />
                        <button onClick={onCancel}>Done</button>
                    </div>
                </div>
            ) : (
                <>
                    {schedule.error && <div className="error meeting-banner">{meetingError(schedule.error)}</div>}

                    <div className="form-grid meeting-popover-form">
                        <label>Product</label>
                        <SelectBox
                            value={productKey}
                            onValueChange={v => {
                                const next = v as ProductKey | "";
                                setProductKey(next);
                                setSelectedProduct(next);
                            }}
                            options={[
                                { value: "", label: "Unassigned" },
                                { value: "live-room", label: "Live Room" },
                                { value: "attend", label: "Attend" },
                            ]}
                        />
                        <label>Title</label>
                        <input value={title} onChange={event => setTitle(event.target.value)} />
                        <label>Starts</label>
                        <DateTimePicker
                            ariaLabel="Meeting start"
                            value={startAt}
                            onChange={nextStartAt => {
                                const next = moveMeetingStart(nextStartAt, startAt, endAt);
                                setStartAt(next.startAt);
                                setEndAt(next.endAt);
                            }}
                        />
                        <label>Ends</label>
                        <DateTimePicker
                            ariaLabel="Meeting end"
                            value={endAt}
                            onChange={setEndAt}
                        />
                        <label>Time zone</label>
                        <input value={timeZone} onChange={event => setTimeZone(event.target.value)} />
                        <label>Guests</label>
                        <textarea
                            value={attendees}
                            onChange={event => setAttendees(event.target.value)}
                            rows={2}
                            placeholder="person@example.com"
                        />
                        <label>Description</label>
                        <textarea value={description} onChange={event => setDescription(event.target.value)} rows={3} />
                    </div>
                    <div className="row meeting-actions">
                        <button
                            className="primary"
                            disabled={
                                !title.trim()
                                || attendeeList.length === 0
                                || !validDates
                                || schedule.isPending
                            }
                            onClick={() => schedule.mutate()}
                        >
                            {schedule.isPending ? "Scheduling…" : "Schedule and send invitation"}
                        </button>
                        {!validDates && <span className="muted">End time must be after start time.</span>}
                    </div>
                </>
            )}
        </div>
    );
}

interface InlineComposerProps {
    ctx:       ComposerCtx;
    onCancel:  () => void;
    onSent:    () => void | Promise<void>;
    onSubjectChange?: (subject: string) => void;
}

function InlineComposer({ ctx, onCancel, onSent, onSubjectChange }: InlineComposerProps) {
    const editorRef = useRef<EmailEditorRef>(null);
    const attachmentInputRef = useRef<HTMLInputElement>(null);
    const attachmentsRef = useRef<UploadedEmailAttachment[]>([]);
    const queryClient = useQueryClient();
    const [recipients, setRecipients] = useState<RecipientBuckets>(() => ({
        ...emptyRecipientBuckets(),
        to: parseRecipients(ctx.to),
        cc: parseRecipients(ctx.cc ?? ""),
        bcc: parseRecipients(ctx.bcc ?? ""),
    }));
    const [subject, setSubject] = useState(ctx.subject);
    const [productKey, setProductKey] = useState<ProductKey | "">(
        ctx.productKey ?? getSelectedProduct(),
    );
    const [bccOutbound, setBccOutboundState] = useState<boolean>(() => getBccOutbound());
    const [sendHtml, setSendHtml] = useState<boolean>(() => getSendAsHtml());
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [attachments, setAttachments] = useState<UploadedEmailAttachment[]>(() => (ctx.draftAttachments ?? []).map(item => ({
        ...item, previewUrl: item.disposition === "inline" ? `${FILES_BASE}/${item.key.split("/").map(encodeURIComponent).join("/")}` : undefined,
    })));
    const [error, setError] = useState<string | null>(null);
    const [suggestedOpen, setSuggestedOpen] = useState(false);
    const [body, setBody] = useState<{ html: string; text: string } | null>(null);
    const [draftStatus, setDraftStatus] = useState("");
    const sendingRef = useRef(false);
    const [autosave] = useState(() => createDraftAutosave<Parameters<typeof messagesApi.saveDraft>[0]>({
        draftId: ctx.draftId,
        onStatus: setDraftStatus,
        save: async (value, draftId) => {
            const result = await messagesApi.saveDraft({ ...value, draftId });
            if (!result.ok || !result.draft) throw new Error(result.error ?? "save_failed");
            queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, (previous = []) =>
                [result.draft!, ...previous.filter(message => message.id !== result.draft!.id)]);
            return result.draft.id;
        },
    }));
    useEffect(() => {
        if (sendingRef.current || !productKey) return;
        if (!body && !subject && !recipients.to.length && !recipients.cc.length && !recipients.bcc.length && !attachments.length) return;
        const editor = editorRef.current?.editor;
        const restoredHtml = ctx.draftBodyHtml ? resolveInlineImages(ctx.draftBodyHtml,
            attachments.filter(item => item.contentId).map(item => ({ contentId: item.contentId!, key: item.key }))) : undefined;
        const prepared = prepareEmailAttachmentsForSend(body?.html ?? editor?.getHTML() ?? restoredHtml, attachments);
        autosave.update({
            productKey, subject, inReplyTo: ctx.inReplyTo ?? undefined,
            toAddress: recipientsToHeader(recipients.to),
            ccAddress: recipientsToHeader(recipients.cc),
            bccAddress: recipientsToHeader(recipients.bcc),
            bodyHtml: prepared.bodyHtml,
            attachments: prepared.attachments,
            bodyText: body?.text ?? editor?.getText(),
        });
    }, [autosave, productKey, subject, recipients, body, attachments, ctx.inReplyTo, ctx.draftBodyHtml]);
    useEffect(() => {
        const warn = (event: BeforeUnloadEvent) => {
            if (!draftStatus || draftStatus === "Draft saved") return;
            event.preventDefault();
            event.returnValue = "";
        };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [draftStatus]);
    useEffect(() => () => { void autosave.flush().catch(() => {}); }, [autosave]);
    async function closeComposer() {
        if (!productKey && (body || subject || recipients.to.length || attachments.length)) {
            setError("Select a product so your draft can be saved before closing.");
            return;
        }
        try { await autosave.flush(); onCancel(); }
        catch { setError("Could not save your draft. Please retry before closing."); }
    }
    // Throw the draft away: stop autosave first so the unmount flush can't
    // write it back, then delete the saved row (if one was ever created).
    async function discardComposer() {
        autosave.stop();
        const id = autosave.id;
        if (id) {
            const result = await messagesApi.deleteDraft(id).catch(() => null);
            if (!result?.ok && result?.error !== "draft_not_found") {
                setError("Could not delete this draft. Please try again.");
                return;
            }
            queryClient.setQueryData<Message[]>(MESSAGES_QUERY_KEY, (previous = []) =>
                previous.filter(message => message.id !== id));
        }
        revokeAttachmentPreviews(attachmentsRef.current);
        onCancel();
    }

    // Caret captured on pointer-down of Insert times / Insert signature, so
    // the insert still lands there after the menu or dialog steals focus.
    const insertAtRef = useRef<EditorPos | null>(null);
    const { data: productData } = useQuery({
        queryKey: ["products"],
        queryFn:  () => productsApi.list(),
        staleTime: 5 * 60_000,
    });

    const { data: contactList } = useQuery({
        queryKey: ["contacts", "all"],
        queryFn:  () => contactsApi.listAll(),
        staleTime: 5 * 60_000,
    });

    // Signature comes from /me/settings — same cache key as the Settings
    // page so edits there propagate immediately. The reply editor mounts
    // once with whatever the cache has at that instant; if you save a
    // signature mid-reply, your in-flight draft isn't disturbed.
    const { data: settings } = useQuery({
        queryKey: settingsQueryKey(productKey || "unassigned"),
        queryFn:  async () => {
            const selected = productKey as ProductKey;
            const res = await userSettingsApi.get(selected);
            return res.settings ?? { signature: null, signatures: [], productKey: selected };
        },
        enabled: Boolean(productKey),
        staleTime: 5 * 60_000,
    });

    const initialDoc = useMemo(
        () => (ctx.draftBodyHtml ? resolveInlineImages(ctx.draftBodyHtml, (ctx.draftAttachments ?? []).filter(item => item.contentId).map(item => ({ contentId: item.contentId!, key: item.key }))) : null) || buildInitialDoc({
            lead:      ctx.quotedLead,
            quoted:    ctx.quotedBody,
            // Auto-insert the default signature on forwards (and the rare
            // standalone) but NOT on replies — the user can drop one in via
            // the "Insert signature" picker if they want it.
            signature: ctx.mode === "forward" ? (settings?.signature ?? null) : null,
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [settings?.signature],
    );

    function insertSignature(record: SignatureRecord) {
        insertSignatureAtCursor(editorRef.current?.editor, record, insertAtRef.current);
    }

    /** Insert the suggested-times block as a single draggable `suggestedTimes`
     *  node at the editor cursor captured before the modal opened. */
    function insertSuggestedTimes(attrs: SuggestedTimesAttrs) {
        const editor = editorRef.current?.editor;
        if (!editor) { setError("Editor not ready."); return; }
        insertAtPos(editor, suggestedTimesInsertContent(attrs), insertAtRef.current);
    }

    useEffect(() => {
        attachmentsRef.current = attachments;
    }, [attachments]);

    useEffect(() => () => {
        revokeAttachmentPreviews(attachmentsRef.current);
    }, []);

    const sendMutation = useMutation({
        mutationFn: async (vars: {
            to: string[];
            cc: string[];
            bcc: string[];
            bodyHtml: string | undefined;
            bodyText: string;
            attachments: EmailAttachment[];
        }) => {
            return messagesApi.sendFullWidth({
                to:        vars.to,
                cc:        vars.cc.length ? vars.cc : undefined,
                bcc:       vars.bcc.length ? vars.bcc : undefined,
                productKey: productKey || undefined,
                subject,
                // Omitting `bodyHtml` makes the server emit a text-only MIME
                // (sendFullWidth.ts builds multipart/alternative only when
                // both parts are present). That's the plain-text path.
                bodyHtml:  vars.bodyHtml,
                bodyText:  vars.bodyText,
                bccOutbound,
                inReplyTo: ctx.inReplyTo ?? undefined,
                campaign: false,
                attachments: vars.attachments,
            });
        },
        // Optimistic: the message lands in the open thread, the Sent view and
        // the flat list immediately. onSuccess swaps in the server row (the
        // realtime echo then dedupes by id); failure restores every cache.
        onMutate: async (vars) => {
            await queryClient.cancelQueries({ queryKey: MESSAGES_QUERY_KEY });
            const rollback = snapshotMailbox(queryClient);
            const tmpId = `tmp-${crypto.randomUUID()}`;
            const nowIso = new Date().toISOString();
            // inReplyTo is the parent's Message-ID header, not its threadKey.
            const parent = ctx.inReplyTo ? findCachedMessageByHeader(queryClient, ctx.inReplyTo) : undefined;
            const optimistic: Message = {
                id:              tmpId,
                productKey:      productKey || null,
                direction:       "outbound",
                status:          "queued",
                threadKey:       parent?.threadKey ?? tmpId,
                fromAddress:     parent ? (parent.direction === "inbound" ? parent.toAddress : parent.fromAddress) : "you",
                fromName:        null,
                toAddress:       vars.to.join(", "),
                ccAddress:       vars.cc.length ? vars.cc.join(", ") : null,
                bccAddress:      vars.bcc.length ? vars.bcc.join(", ") : null,
                subject,
                bodyHtml:        vars.bodyHtml ?? null,
                bodyText:        vars.bodyText ?? null,
                inReplyTo:       ctx.inReplyTo ?? null,
                messageIdHeader: null,
                toName:          null,
                sentAt:          nowIso,
                receivedAt:      null,
                createdAt:       nowIso,
            };
            upsertMailboxMessages(queryClient, [optimistic]);
            return { rollback, tmpId, newThread: !parent };
        },
        onError: (err: any, _vars, c) => {
            sendingRef.current = false;
            c?.rollback();
            setError(String(err?.body ?? err?.message ?? err));
        },
        onSuccess: (result: any, _vars, c) => {
            if (!result?.ok) {
                sendingRef.current = false;
                c?.rollback();
                // A partial group send still delivered some copies; the
                // realtime messages:created events add those back.
                setError(`${result?.error ?? "send_failed"}${result?.details ? `: ${result.details}` : ""}`);
                return;
            }
            // Swap the tmp row for the canonical server row(s) — real id,
            // messageIdHeader and threadKey.
            const rows: Message[] = (result.messages as Message[] | undefined)?.length
                ? result.messages
                : result.message ? [result.message] : [];
            if (c) {
                if (c.newThread) {
                    // The placeholder thread key was the tmp id; the server minted the real one.
                    removeMailboxMessage(queryClient, c.tmpId);
                    removeMailboxThread(queryClient, c.tmpId);
                } else if (rows[0]) {
                    replaceMailboxMessage(queryClient, c.tmpId, rows[0]);
                }
                // Group sends store one row per recipient, each possibly its own thread.
                for (const row of c.newThread ? rows : rows.slice(1)) upsertMailboxMessages(queryClient, [row]);
            }
            revokeAttachmentPreviews(attachmentsRef.current);
            setAttachments([]);
            attachmentsRef.current = [];
            autosave.stop();
            if (autosave.id) {
                const draftId = autosave.id;
                removeMailboxMessage(queryClient, draftId);
                void messagesApi.deleteDraft(draftId).catch(() => {});
            }
            onSent();
        },
    });

    const uploadInlineImage = useCallback(async (file: File) => {
        setError(null);
        setUploadingAttachment(true);
        try {
            validateAttachmentSelection(attachmentsRef.current, [file]);
            const attachment = await uploadEmailAttachment(file, "inline");
            setAttachments(previous => {
                const next = [...previous, attachment];
                attachmentsRef.current = next;
                return next;
            });
            return { url: attachment.previewUrl! };
        } catch (err: any) {
            setError(err?.message ?? String(err));
            throw err;
        } finally {
            setUploadingAttachment(false);
        }
    }, []);

    async function addFileAttachments(files: FileList | null) {
        const selected = files ? Array.from(files) : [];
        if (!selected.length) return;
        setError(null);
        setUploadingAttachment(true);
        try {
            validateAttachmentSelection(attachmentsRef.current, selected);
            for (const file of selected) {
                const attachment = await uploadEmailAttachment(file, "attachment");
                setAttachments(previous => {
                    const next = [...previous, attachment];
                    attachmentsRef.current = next;
                    return next;
                });
            }
        } catch (err: any) {
            setError(err?.message ?? String(err));
        } finally {
            setUploadingAttachment(false);
            if (attachmentInputRef.current) attachmentInputRef.current.value = "";
        }
    }

    function removeAttachment(attachment: UploadedEmailAttachment) {
        removeEditorImageBySrc(editorRef.current?.editor, attachment.previewUrl);
        if (attachment.previewUrl) URL.revokeObjectURL(attachment.previewUrl);
        setAttachments(previous => {
            const next = previous.filter(item => item.key !== attachment.key);
            attachmentsRef.current = next;
            return next;
        });
    }

    async function send() {
        if (!editorRef.current || sendingRef.current) return;
        if (!productKey) { setError("Select a product before sending."); return; }
        // Pull in whatever is still sitting in the recipient inputs unconfirmed.
        const to  = recipients.to.map((r) => recipientsToHeader([r])!);
        const cc  = recipients.cc.map((r) => recipientsToHeader([r])!);
        const bcc = recipients.bcc.map((r) => recipientsToHeader([r])!);
        if (!to.length && !cc.length && !bcc.length) {
            setError("Add at least one recipient."); return;
        }
        if (!subject.trim()) { setError("Subject is required."); return; }
        setError(null);
        sendingRef.current = true;
        try { await autosave.flush(); }
        catch { sendingRef.current = false; setError("Could not save draft. Retry before sending."); return; }
        try {
            const bodyHtml = sendHtml ? await editorRef.current!.getEmailHTML() : undefined;
            const bodyText = await editorRef.current!.getEmailText();
            const prepared = prepareEmailAttachmentsForSend(bodyHtml, attachments);
            sendMutation.mutate({ to, cc, bcc, bodyHtml: prepared.bodyHtml, bodyText, attachments: prepared.attachments });
        } catch (error) {
            sendingRef.current = false;
            setError(`Could not read email: ${String(error)}`);
        }
    }
    const busy = sendMutation.isPending || uploadingAttachment;

    const heading = ctx.draftId ? "Draft"
        : ctx.mode === "forward" ? "Forward"
        : ctx.mode === "replyAll" ? "Reply all"
        : ctx.mode === "reply" ? "Reply"
        : "New email";

    return (
        <div className="inline-composer">
            <div className="inline-composer-head">
                <strong>{heading}</strong>
                <span role="status" className="muted">{!productKey ? "Select a product to save drafts" : draftStatus}</span>
                <span className="spacer" />
                <Button variant="ghost" size="sm" className="inline-composer-cancel" onClick={discardComposer} disabled={busy} aria-label="Discard draft" title="Discard draft">
                    <Trash2 aria-hidden="true" />
                </Button>
                <Button variant="ghost" size="sm" className="inline-composer-cancel" onClick={closeComposer} disabled={busy} aria-label="Close draft" title="Close and keep draft">
                    <X aria-hidden="true" />
                </Button>
                <button className="primary inline-composer-head-send" onClick={send} disabled={busy || !productKey}>
                    <SendHorizontal aria-hidden="true" />
                    <span>{busy ? "Sending…" : "Send"}</span>
                </button>
            </div>

            {error && <div className="error" style={{ margin: "8px 0" }}>{error}</div>}

            <div className="inline-composer-row">
                <label>Product</label>
                <SelectBox
                    value={productKey}
                    className="w-auto"
                    style={{ minWidth: 220 }}
                    disabled={Boolean(ctx.inReplyTo)}
                    onValueChange={v => {
                        const next = v as ProductKey | "";
                        setProductKey(next);
                        setSelectedProduct(next);
                    }}
                    options={[
                        { value: "", label: "Select a product" },
                        ...(productData?.products ?? []).map(product => ({
                            value: product.key,
                            label: `${product.name}${product.configured ? "" : " (not configured)"}`,
                            disabled: !product.configured,
                        })),
                    ]}
                />
            </div>
            <div className="inline-composer-recipients">
                <RecipientEditor
                    value={recipients}
                    onChange={setRecipients}
                    contacts={contactList}
                    labelWidth={60}
                    colGap={8}
                />
            </div>
            <div className="inline-composer-row">
                <label>Subject</label>
                <input value={subject} onChange={(e) => { setSubject(e.target.value); onSubjectChange?.(e.target.value); }} />
            </div>

            <div className="compose-body-wrap">
                {/* Keep the editor mounted when settings arrive to preserve typed text. */}
                <RichEmailEditor
                    ref={editorRef}
                    content={initialDoc as any}
                    onContentChange={(html, text) => setBody({ html, text })}
                    onUploadImage={uploadInlineImage}
                    onInsertSuggestedTimes={pos => {
                        insertAtRef.current = pos ?? captureEditorPos(editorRef.current?.editor);
                        setSuggestedOpen(true);
                    }}
                />
            </div>

            <EmailAttachmentList attachments={attachments} onRemove={removeAttachment} />

            <div className="inline-composer-footer">
                <input
                    ref={attachmentInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={event => void addFileAttachments(event.target.files)}
                />
                <button className="primary inline-composer-footer-send" onClick={send} disabled={busy || !productKey}>
                    {busy ? "Sending…" : "Send"}
                </button>
                <button
                    type="button"
                    className="inline-composer-attach"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={busy}
                >
                    <Paperclip aria-hidden="true" />
                    <span>Attach</span>
                </button>
                <SignaturePicker
                    signatures={settings?.signatures ?? []}
                    onPick={insertSignature}
                    disabled={busy}
                    onCaptureCursor={() => {
                        insertAtRef.current = captureEditorPos(editorRef.current?.editor);
                    }}
                />
                <span className="spacer" />
                <Menu.Root>
                    <Menu.Trigger
                        render={<Button variant="ghost" size="sm" className="inline-composer-more" aria-label="More send options" />}
                    >
                        <Ellipsis aria-hidden="true" />
                    </Menu.Trigger>
                    <Menu.Content align="end">
                        <Menu.Group>
                            <Menu.GroupLabel>Send options</Menu.GroupLabel>
                            <Menu.Item onClick={() => {
                                const next = !sendHtml;
                                setSendHtml(next);
                                setSendAsHtml(next);
                            }}>
                                <Check className="inline-composer-option-check" style={{ visibility: sendHtml ? "visible" : "hidden" }} aria-hidden="true" />
                                Send as HTML
                            </Menu.Item>
                            <Menu.Item onClick={() => {
                                const next = !bccOutbound;
                                setBccOutboundState(next);
                                setBccOutbound(next);
                            }}>
                                <Check className="inline-composer-option-check" style={{ visibility: bccOutbound ? "visible" : "hidden" }} aria-hidden="true" />
                                BCC product archive
                            </Menu.Item>
                        </Menu.Group>
                    </Menu.Content>
                </Menu.Root>
            </div>

            <SuggestedTimesModal
                open={suggestedOpen}
                onOpenChange={setSuggestedOpen}
                onInsert={insertSuggestedTimes}
            />
        </div>
    );
}
